import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Paper,
  LinearProgress,
  Divider,
  IconButton,
  Avatar,
  Collapse,
  Switch,
  Tooltip,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Business,
  AccountBalance,
  PictureAsPdf,
  InsertDriveFile,
  CheckCircle,
  Error as ErrorIcon,
  Download,
  Visibility,
  Send,
  Refresh,
  CalendarToday,
  Info,
  Email,
  Phone,
  LocationOn,
  ExpandMore,
  ChevronRight,
  Add,
  Edit,
  DeleteOutline,
  People,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx-js-style';
import MizanService from '../../services/mizanService';
import AuthService from '../../services/authService';
import RuleService from '../../services/ruleService';
import { useCompany } from '../../contexts/CompanyContext';
import UploadedTrialBalanceFilesList from './UploadedTrialBalanceFilesList';
import { EDEFTER_API_URL } from '../../config/edefterApi';

const MizanReading = ({
  hideUploadedFilesList = false,
  externalViewingMizanId = null,
  externalViewingFileName = '',
  externalViewingDonem = null,
  externalViewingDateRange = null,
  externalViewingPeriodStart = null,
  externalViewingPeriodEnd = null,
  externalViewingRefreshToken = 0,
  embedded = false,
  uploadedMizansRefreshToken = 0,
} = {}) => {
  const { selectedCompany, companyId, taxId } = useCompany();
  const [selectedFile, setSelectedFile] = useState(null);
  const [mizanData, setMizanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Uploaded files list refresh trigger (child component uses it)
  const [uploadedListRefreshToken, setUploadedListRefreshToken] = useState(0);
  const [selectedMizanDetail, setSelectedMizanDetail] = useState(null);
  const [selectedMizanListMeta, setSelectedMizanListMeta] = useState(null);
  const [viewingMizanId, setViewingMizanId] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set()); // Açık olan account code'lar
  const [expandedYevmiyeRules, setExpandedYevmiyeRules] = useState(new Set()); // Açık olan kural grupları
  const [yevmiyeData, setYevmiyeData] = useState(null);
  const [agacData, setAgacData] = useState(null);
  const [rules, setRules] = useState([]);
  const [mizanTabValue, setMizanTabValue] = useState(0);
  const [loadingRules, setLoadingRules] = useState(false);
  const [ruleForm, setRuleForm] = useState({
    id: null,
    aktaranKod: '',
    aktaranYon: 'Alacak',
    kosul: '',
    tutar: '',
    aktarilanKod: '',
    aktarilanYon: 'Borç',
      aciklama: '',
      aktif: false,
      oncelik: 100,
  });
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [showRuleInfo, setShowRuleInfo] = useState(false);
  const [showTransferCleaningRules, setShowTransferCleaningRules] = useState(false);
  const [showTrialBalanceSection, setShowTrialBalanceSection] = useState(true);

  // company_shareholders (ortaklar)
  const [companyShareholdersRaw, setCompanyShareholdersRaw] = useState([]);
  const [companyShareholdersLoading, setCompanyShareholdersLoading] = useState(false);
  const [companyShareholdersError, setCompanyShareholdersError] = useState(null);
  const [selectedMizanPeriodYear, setSelectedMizanPeriodYear] = useState(null);

  const iliskiliTarafRows = useMemo(() => {
    const list = Array.isArray(companyShareholdersRaw) ? companyShareholdersRaw : [];
    const toBool = (v) => {
      if (v === true || v === 1) return true;
      if (typeof v === 'string') return v === '1' || v.toLowerCase() === 'true';
      return false;
    };

    return list
      .map((row) => ({
        id: row.id,
        tur: toBool(row.isGroupCompany ?? row.is_group_company) ? 'Grup Firma' : 'Ortak',
        adSoyad: [row.firstName ?? row.first_name, row.lastName ?? row.last_name].filter(Boolean).join(' ').trim(),
        payOrani: row.shareRatio ?? row.share_ratio ?? '',
      }))
      .filter((row) => row.adSoyad)
      .sort((a, b) => (a.adSoyad || '').localeCompare(b.adSoyad || '', 'tr', { sensitivity: 'base' }));
  }, [companyShareholdersRaw]);

  useEffect(() => {
    // İlişkili Taraflar tabı (index=4) seçilince yükle
    if (mizanTabValue !== 4 || !companyId) {
      return;
    }
    let cancelled = false;
    const load = async () => {
      setCompanyShareholdersLoading(true);
      setCompanyShareholdersError(null);
      try {
        const payload = {
          companyId: String(companyId),
          periodYear:
            selectedMizanPeriodYear != null && selectedMizanPeriodYear !== ''
              ? Number(selectedMizanPeriodYear)
              : null,
        };
        const res = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/company-shareholders/query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          if (!cancelled) {
            setCompanyShareholdersRaw([]);
            if (res.status !== 404) {
              setCompanyShareholdersError(`Ortak listesi yüklenemedi (${res.status})`);
            }
          }
          return;
        }
        const data = await res.json();
        const list = Array.isArray(data) ? data : data?.items || data?.data || data?.shareholders || [];
        if (!cancelled) setCompanyShareholdersRaw(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) {
          setCompanyShareholdersRaw([]);
          setCompanyShareholdersError(e?.message || 'Ortak listesi yüklenemedi');
        }
      } finally {
        if (!cancelled) setCompanyShareholdersLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [mizanTabValue, companyId, selectedMizanPeriodYear]);

  const currentUser = AuthService.getCurrentUser();

  // Kuralları çek
  const fetchRules = async () => {
    setLoadingRules(true);
    try {
      const data = await RuleService.list();
      setRules(data || []);
    } catch (err) {
      console.error('Fetch rules error:', err);
      toast.error('Failed to load rules');
    } finally {
      setLoadingRules(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const requestUploadedListRefresh = () => {
    setUploadedListRefreshToken((t) => t + 1);
  };

  // Lazy load agacData when Adjusted Trial Balance tab is opened
  useEffect(() => {
    if (mizanTabValue === 1 && !agacData && selectedMizanDetail?._agacListPending) {
      // Load agacData only when tab is opened to improve initial load performance
      setAgacData(selectedMizanDetail._agacListPending);
      // Clean up the pending data
      delete selectedMizanDetail._agacListPending;
    }
  }, [mizanTabValue, agacData, selectedMizanDetail]);

  const getYearFromDonem = (donemValue) => {
    const donem = String(donemValue || '').trim();
    if (!donem) return null;
    // mm/dd/yyyy format
    const mdyMatch = donem.match(/^\d{1,2}\/\d{1,2}\/(\d{4})$/);
    if (mdyMatch) return Number(mdyMatch[1]);
    // Fallback: if year-only value ever comes
    if (/^\d{4}$/.test(donem)) return Number(donem);
    return null;
  };

  // View trial balance details
  const handleViewMizan = async (mizanId, periodYearArg = null, selectedMeta = null) => {
    setViewingMizanId(mizanId);
    setSelectedMizanPeriodYear(periodYearArg);
    setSelectedMizanListMeta(selectedMeta || null);
    // Yeni kayıt yüklenirken eski veriyi göstermeyelim
    setMizanData(null);
    setYevmiyeData(null);
    setAgacData(null);
    setLoading(true);
    try {
      const isPeriodMizanId = typeof mizanId === 'number' || /^\d+$/.test(String(mizanId || ''));
      const result = isPeriodMizanId
        ? await MizanService.getPeriodMizanById(mizanId)
        : await MizanService.getMizanById(mizanId);
      if (result.success) {
        setSelectedMizanDetail(result.data);
        const derivedPeriodYear =
          periodYearArg ??
          result?.data?.periodYear ??
          result?.data?.year ??
          (() => {
            const d = result?.data?.periodCoveredStart;
            if (!d) return null;
            const m = String(d).match(/^(\d{4})/);
            return m ? Number(m[1]) : null;
          })();
        setSelectedMizanPeriodYear(derivedPeriodYear);
        const mizanList = result.data.mizanList || [];
       
        // Batch state updates to prevent multiple re-renders
        setTimeout(() => {
          setMizanData(mizanList);
          const yevmiyeList = result.data.yevmiyeList || [];
          setYevmiyeData(yevmiyeList);
          // agacData will be loaded lazily when tab is opened
          if (result.data.agacList && result.data.agacList.length > 0) {
            // Store in selectedMizanDetail, will be loaded when tab opens
            result.data._agacListPending = result.data.agacList;
          }
          setAgacData(null);
        }, 0);
      } else {
        toast.error(result.error || 'Failed to load trial balance details.');
      }
    } catch (err) {
      console.error('View trial balance error:', err);
      toast.error('Failed to load trial balance details.');
    } finally {
      setLoading(false);
    }
  };

  // TrialBalance from file tab'ındaki liste tıklamasıyla dışarıdan seçilen mizanı viewer'a yükle
  useEffect(() => {
    if (!externalViewingMizanId) return;
    handleViewMizan(externalViewingMizanId, getYearFromDonem(externalViewingDonem), {
      donem: externalViewingDonem,
      tarihAraligi: externalViewingDateRange,
      periodCoveredStart: externalViewingPeriodStart,
      periodCoveredEnd: externalViewingPeriodEnd,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    externalViewingMizanId,
    externalViewingRefreshToken,
    externalViewingDonem,
    externalViewingDateRange,
    externalViewingPeriodStart,
    externalViewingPeriodEnd,
  ]);

  // Download trial balance
  const handleDownloadMizan = async (mizanId, timestamp) => {
    if (!companyId) {
      toast.warning('Please enter Company ID.');
      return;
    }
    
    setLoading(true);
    try {
      const result = await MizanService.downloadMizan(companyId, mizanId, timestamp);
      if (result.success && result.blob) {
        // Download blob
        const url = window.URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mizan_${mizanId}_${timestamp}.${result.blob.type.includes('pdf') ? 'pdf' : 'xlsx'}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Trial balance downloaded successfully.');
      } else {
        toast.error(result.error || 'Failed to download trial balance.');
      }
    } catch (err) {
      console.error('Download trial balance error:', err);
      toast.error('Failed to download trial balance.');
    } finally {
      setLoading(false);
    }
  };

  // Rule handlers
  const resetRuleForm = () => {
    setRuleForm({
      id: null,
      aktaranKod: '',
      aktaranYon: 'Alacak',
      kosul: '',
      tutar: '',
      aktarilanKod: '',
      aktarilanYon: 'Borç',
      aciklama: '',
      aktif: false,
      oncelik: 100,
    });
  };

  const handleEditRule = (rule) => {
    setRuleForm({
      id: rule.id,
      aktaranKod: rule.aktaranKod || '',
      aktaranYon: rule.aktaranYon || 'Alacak',
      kosul: rule.kosul || '',
      tutar: rule.tutar || '',
      aktarilanKod: rule.aktarilanKod || '',
      aktarilanYon: rule.aktarilanYon || 'Borç',
      aciklama: rule.aciklama || '',
      aktif: rule.aktif ?? false,
      oncelik: rule.oncelik ?? 100,
    });
    setShowRuleForm(true);
  };

  const handleDeleteRule = async (id) => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    try {
      await RuleService.delete(id);
      toast.success('Rule deleted');
      fetchRules();
      if (ruleForm.id === id) resetRuleForm();
      
      // Eğer bir mizan görüntüleniyorsa, kurallar değiştiği için yeniden yükle
      if (viewingMizanId) {
        await handleViewMizan(viewingMizanId, selectedMizanPeriodYear);
      }
    } catch (err) {
      console.error('Delete rule error:', err);
      toast.error(err.message || 'Failed to delete rule');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      const updated = await RuleService.setActive(rule.id, !rule.aktif);
      setRules(prev => prev.map(r => r.id === rule.id ? updated : r));
      
      // Eğer bir mizan görüntüleniyorsa, kurallar değiştiği için yeniden yükle
      if (viewingMizanId) {
        await handleViewMizan(viewingMizanId, selectedMizanPeriodYear);
      }
    } catch (err) {
      console.error('Toggle rule error:', err);
      toast.error(err.message || 'Failed to update status');
    }
  };

  const handleRuleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...ruleForm };
    try {
      if (payload.id) {
        await RuleService.update(payload.id, payload);
        toast.success('Rule updated');
      } else {
        await RuleService.create(payload);
        toast.success('Rule added');
      }
      resetRuleForm();
      fetchRules();
      setShowRuleForm(false);
      
      // Eğer bir mizan görüntüleniyorsa, kurallar değiştiği için yeniden yükle
      if (viewingMizanId) {
        await handleViewMizan(viewingMizanId, selectedMizanPeriodYear);
      }
    } catch (err) {
      console.error('Save rule error:', err);
      toast.error(err.message || 'Failed to save rule');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls', '.pdf'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!validExtensions.includes(fileExtension)) {
        toast.error('Please select an Excel (.xlsx, .xls) or PDF (.pdf) file.');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      setSuccess(false);
      setMizanData(null);
      setAgacData(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast.warning('Please select a file.');
      return;
    }

    if (!companyId || !taxId) {
      toast.warning('Please enter Company ID and Tax Number.');
      return;
    }

    if (!currentUser?.id) {
      toast.error('User information not found. Please login again.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);
    setMizanData(null);
    setYevmiyeData(null);
    setAgacData(null);

    try {
      // Progress tracking - upload 0-30%
      const xhr = new XMLHttpRequest();
      let uploadProgressValue = 0;
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          uploadProgressValue = Math.round((e.loaded / e.total) * 30);
          setUploadProgress(uploadProgressValue);
        }
      });

      // Upload dosyayı gönder ve jobId al
      const uploadResult = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const responseData = JSON.parse(xhr.responseText);
              resolve(responseData);
            } catch (parseError) {
              resolve(xhr.responseText);
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.error || errorData || 'Upload failed'));
            } catch {
              reject(new Error(xhr.statusText || 'Upload failed'));
            }
          }
        };
        
        xhr.onerror = () => {
          reject(new Error('Network error'));
        };

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('userId', currentUser.id);
        formData.append('companyId', companyId);
        formData.append('taxId', taxId);

        xhr.open('POST', `${EDEFTER_API_URL}/api/mizan/upload`);
        const token = AuthService.getAuthToken();
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.send(formData);
      });

      // JobId alındı, şimdi polling başlat
      if (uploadResult.jobId) {
        setUploadProgress(30); // Upload tamamlandı
        
        // Polling ile job durumunu kontrol et
        await pollJobStatus(uploadResult.jobId);
      } else {
        // Eski format (senkron response) - geriye dönük uyumluluk
        if (uploadResult.mizanList) {
          setTimeout(() => {
            setMizanData(uploadResult.mizanList || []);
            setYevmiyeData(uploadResult.yevmiyeList || []);
            setSelectedMizanDetail(uploadResult);
            setAgacData(null);
            setSuccess(true);
          }, 0);
          toast.success('Trial balance uploaded and parsed successfully.');
          if (companyId) {
            setTimeout(() => {
              requestUploadedListRefresh();
            }, 100);
          }
        } else {
          setError('Unexpected response format.');
          toast.error('An error occurred while uploading trial balance.');
        }
      }
    } catch (err) {
      console.error('Upload trial balance error:', err);
      setError(err.message || 'An error occurred while uploading mizan.');
      toast.error(err.message || 'An error occurred while uploading mizan.');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  // Job durumunu polling ile kontrol et
  const pollJobStatus = async (jobId) => {
    const maxAttempts = 300; // 5 dakika (1 saniye aralıklarla)
    let attempts = 0;
    let currentProgress = 30;

    const pollInterval = setInterval(async () => {
      attempts++;
      
      try {
        const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/api/mizan/upload/${jobId}/status`);
        if (response.ok) {
          const statusData = await response.json();
          const status = statusData.status;

          // Progress güncelle (30-100% arası)
          if (status === 'PROCESSING') {
            currentProgress = Math.min(30 + Math.round((attempts / maxAttempts) * 60), 95);
            setUploadProgress(currentProgress);
          } else if (status === 'COMPLETED') {
            clearInterval(pollInterval);
            setUploadProgress(100);
            
            // İşlem tamamlandı, mizan listesini yenile
            toast.success('Trial balance uploaded and parsed successfully.');
            if (companyId) {
              setTimeout(() => {
                requestUploadedListRefresh();
              }, 100);
            }
            setSuccess(true);
            setLoading(false);
          } else if (status === 'FAILED') {
            clearInterval(pollInterval);
            const errMsg = statusData.errorMessage || '';

            setError(errMsg || 'Mizan işleme başarısız oldu.');
            toast.error(errMsg || 'Mizan işleme başarısız oldu.');
            setLoading(false);
          }
        }

        // Maksimum deneme sayısına ulaşıldı
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setError('Mizan işleme zaman aşımına uğradı. Lütfen tekrar deneyin.');
          toast.error('Mizan işleme zaman aşımına uğradı.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Job status polling error:', err);
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setError('Job durumu kontrol edilemedi.');
          setLoading(false);
        }
      }
    }, 1000); // Her 1 saniyede bir kontrol et
  };

  const ruleFormContent = (
    <Box component="form" onSubmit={handleRuleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <TextField
        fullWidth
        label="Source Account Code"
        placeholder="Example: 101"
        size="small"
        value={ruleForm.aktaranKod}
        onChange={(e) => setRuleForm({ ...ruleForm, aktaranKod: e.target.value })}
        required
      />
      <TextField
        fullWidth
        label="Source Side"
        placeholder="Debit or Credit"
        size="small"
        value={ruleForm.aktaranYon}
        onChange={(e) => setRuleForm({ ...ruleForm, aktaranYon: e.target.value })}
        required
      />
      <TextField
        fullWidth
        label="Condition"
        placeholder='Example: hasSubAccountWithName("101", "2024 VADELI ÇEKLER")'
        size="small"
        value={ruleForm.kosul}
        onChange={(e) => setRuleForm({ ...ruleForm, kosul: e.target.value })}
        required
        multiline
        rows={2}
      />
      <TextField
        fullWidth
        label="Amount Expression"
        placeholder='Example: sumSubAccountBalance("101", "2024 VADELI ÇEKLER", "alacak")'
        size="small"
        value={ruleForm.tutar}
        onChange={(e) => setRuleForm({ ...ruleForm, tutar: e.target.value })}
        required
        multiline
        rows={2}
      />
      <TextField
        fullWidth
        label="Target Account Code"
        placeholder="Example: 102"
        size="small"
        value={ruleForm.aktarilanKod}
        onChange={(e) => setRuleForm({ ...ruleForm, aktarilanKod: e.target.value })}
        required
      />
      <TextField
        fullWidth
        label="Target Side"
        placeholder="Debit or Credit"
        size="small"
        value={ruleForm.aktarilanYon}
        onChange={(e) => setRuleForm({ ...ruleForm, aktarilanYon: e.target.value })}
        required
      />
      <TextField
        fullWidth
        label="Description"
        size="small"
        value={ruleForm.aciklama}
        onChange={(e) => setRuleForm({ ...ruleForm, aciklama: e.target.value })}
      />
      <TextField
        fullWidth
        label="Priority (lower runs first)"
        size="small"
        type="number"
        value={ruleForm.oncelik}
        onChange={(e) => setRuleForm({ ...ruleForm, oncelik: parseInt(e.target.value || '0', 10) })}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button 
          type="submit" 
          variant="contained" 
          startIcon={ruleForm.id ? <Edit /> : <Add />}
          sx={{
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(16, 38, 166) 0%, rgba(14, 33, 148) 50%, rgba(12, 28, 130) 100%)',
            boxShadow: '0 4px 15px rgba(16, 38, 166, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(20, 45, 180, 1) 0%, rgba(18, 40, 170, 1) 50%, rgba(16, 38, 166) 100%)',
              boxShadow: '0 6px 20px rgba(16, 38, 166, 0.6)',
            }
          }}
        >
          {ruleForm.id ? 'Update Rule' : 'Add Rule'}
        </Button>
        <Button 
          color="inherit" 
          onClick={() => { resetRuleForm(); setShowRuleForm(false); }}
          sx={{
            borderRadius: 3
          }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0.00';
    const number = parseFloat(value);
    if (isNaN(number)) return '0.00';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  // Backend alt kırılımı çoğunlukla `subId` içinde noktalı formatta tutuyor (örn: hesapKodu=100, subId=100.01).
  // UI nesting'i yaparken bu alanı "effective" hiyerarşik kod olarak kullanacağız.
  const getRowHierarchyCode = (row) => {
    const hesapKodu = String(row?.hesapKodu || '').trim();
    const subId = String(row?.subId || '').trim();

    // subId, hesapKodu altında bir alt kırılım ise onu kullan.
    // Örn: hesapKodu="100" iken subId="100.01" => "100.01" hiyerarşide child kabul edilecek.
    if (subId && hesapKodu && subId !== hesapKodu && subId.startsWith(`${hesapKodu}.`)) {
      return subId;
    }
    return hesapKodu;
  };

  const getCompanyDisplayName = () => {
    const raw =
      selectedCompany?.name ||
      selectedCompany?.title ||
      selectedCompany?.companyName ||
      selectedCompany?.unvan ||
      '';
    return String(raw).trim();
  };

  // Mizan verilerini account code'a göre grupla
  // Hesap kodları prefix ilişkisine göre iç içe yapılır (örn: 101 ana, 101.2024 ve 101.2025 alt)
  const groupedMizanData = useMemo(() => {
    if (!mizanData || !Array.isArray(mizanData) || mizanData.length === 0) return null;

    // Debug için belirli hesap kodlarını izle (console'a basılır).
    // Not: Default sadece '120'. İstersen burayı çoğaltabilirsin: new Set(['120','121'])
    const DEBUG_ACCOUNT_CODES = new Set(['120']);

    const groups = new Map();
    const processedCodes = new Set();
    const codeSet = new Set(); // Hızlı lookup için

    // Önce tüm satırları hesap koduna göre grupla ve kod setini oluştur
    mizanData.forEach((row) => {
      const accountCode = row.hesapKodu || '';
      if (accountCode) {
        codeSet.add(accountCode);
        if (!groups.has(accountCode)) {
          groups.set(accountCode, {
            accountCode,
            accountName: row.hesapAdi || '',
            baseRow: row,
            subRows: [],
          });
        } else {
          // Aynı hesap koduna sahip birden fazla satır varsa, diğerlerini subRow olarak ekle
          const group = groups.get(accountCode);
          group.subRows.push(row);
        }
      }
    });

    // Prefix ilişkilerini bul ve alt kodları ana kodlara bağla - OPTİMİZE EDİLMİŞ VERSİYON
    // Her hesap kodunu sadece bir kez işleyerek O(n) yaklaşımı
    const parentChildMap = new Map(); // parent -> [children] mapping
    
    // Önce tüm parent-child ilişkilerini bul (O(n) yaklaşımı)
    codeSet.forEach(code => {
      // Her kod için olası parent'ları bul
      const parts = code.split('.');
      for (let i = parts.length - 1; i > 0; i--) {
        const potentialParent = parts.slice(0, i).join('.');
        if (codeSet.has(potentialParent)) {
          if (!parentChildMap.has(potentialParent)) {
            parentChildMap.set(potentialParent, []);
          }
          parentChildMap.get(potentialParent).push(code);
          break; // Sadece direkt parent'ı bul (en yakın parent)
        }
      }
    });

    // Parent-child ilişkilerini kullanarak subRows'ları ekle
    // Tüm alt seviyeleri (recursive) eklemek için helper function
    const addAllDescendants = (parentCode, parentGroup) => {
      const directChildren = parentChildMap.get(parentCode) || [];
      directChildren.forEach(childCode => {
        if (!processedCodes.has(childCode)) {
          const childGroup = groups.get(childCode);
          if (childGroup) {
            // Alt kodun baseRow'unu parent'ın subRows'una ekle
            parentGroup.subRows.push(childGroup.baseRow);
            // Alt kodun kendi subRows'larını da ekle (eğer varsa)
            if (childGroup.subRows.length > 0) {
              parentGroup.subRows.push(...childGroup.subRows);
            }
            processedCodes.add(childCode);
            
            // Recursive: Bu child'ın da alt seviyeleri varsa onları da parent'a ekle
            if (parentChildMap.has(childCode)) {
              // Child'ın alt seviyelerini de parent'ın subRows'una ekle
              addAllDescendants(childCode, parentGroup);
            }
          }
        }
      });
    };

    parentChildMap.forEach((childCodes, parentCode) => {
      const parentGroup = groups.get(parentCode);
      if (parentGroup) {
        addAllDescendants(parentCode, parentGroup);
      }
    });
    
    // Debug log: hedef hesap için alt satırlar nasıl geliyor?
    DEBUG_ACCOUNT_CODES.forEach((targetCode) => {
      const targetGroup = groups.get(targetCode);
      if (!targetGroup) return;

      const subRows = Array.isArray(targetGroup.subRows) ? targetGroup.subRows : [];
      const altCandidates = subRows
        .filter((r) => (r?.subId || '').trim() !== '' && (r?.subId || '').trim() !== (r?.hesapKodu || '').trim())
        .slice(0, 50)
        .map((r) => ({
          hesapKodu: r?.hesapKodu || '',
          subId: r?.subId || '',
          borcBakiyesi: r?.borcBakiyesi ?? r?.borc ?? null,
        }));

      const nonPrefixedAlt = subRows
        .filter((r) => {
          const rowCode = (r?.hesapKodu || '').trim();
          const subId = (r?.subId || '').trim();
          return subId && rowCode === targetCode; // alt kod hesapKodu'nda nokta yok => parentCode + '.' ile eşleşmez
        })
        .slice(0, 50)
        .map((r) => ({
          hesapKodu: r?.hesapKodu || '',
          subId: r?.subId || '',
          hesapAdi: r?.hesapAdi || '',
        }));
    });

    // Ana grupları oluştur (işlenmemiş kodlar - yani alt kod olmayanlar)
    return Array.from(groups.entries())
      .filter(([code]) => !processedCodes.has(code))
      .map(([code, group]) => {
        // Toplam hesaplaması için sadece direkt child'ları kullan (bilanço doğruluğu için)
        // Görüntüleme için tüm alt seviyeler subRows'da zaten var
        const directChildRows = group.subRows.filter((row) => {
          const rowCode = getRowHierarchyCode(row);
          if (!rowCode.startsWith(code + '.')) return false;
          // Effective direct child: arada başka bir ara seviye (node) yoksa direkt çocuk sayılır
          // Örn: 120 → 120.01.002 arasında 120.01 yoksa, 120.01.002 direkt çocuktur
          return !group.subRows.some(other => {
            const otherCode = getRowHierarchyCode(other);
            return otherCode !== rowCode
              && otherCode.startsWith(code + '.')
              && rowCode.startsWith(otherCode + '.');
          });
        });

        const totalsFromSubRows = directChildRows.reduce(
          (acc, row) => {
            acc.borcToplami += parseFloat(row.borcToplami) || 0;
            acc.alacakToplami += parseFloat(row.alacakToplami) || 0;
            acc.borcBakiyesi += parseFloat(row.borcBakiyesi) || 0;
            acc.alacakBakiyesi += parseFloat(row.alacakBakiyesi) || 0;
            return acc;
          },
          { borcToplami: 0, alacakToplami: 0, borcBakiyesi: 0, alacakBakiyesi: 0 }
        );

        const hasMultipleDetails = group.subRows.length > 1;
        const hasAnyDetail = group.subRows.length > 0;
        const hasDirectChildren = directChildRows.length > 0;

        return {
          ...group,
          // Toplamları sadece direkt child'lardan hesapla (bilanço doğruluğu için)
          totalBorcToplami: hasDirectChildren ? totalsFromSubRows.borcToplami : (group.baseRow?.borcToplami || 0),
          totalAlacakToplami: hasDirectChildren ? totalsFromSubRows.alacakToplami : (group.baseRow?.alacakToplami || 0),
          totalBorcBakiyesi: hasDirectChildren ? totalsFromSubRows.borcBakiyesi : (group.baseRow?.borcBakiyesi || 0),
          totalAlacakBakiyesi: hasDirectChildren ? totalsFromSubRows.alacakBakiyesi : (group.baseRow?.alacakBakiyesi || 0),
          hasMultipleDetails,
        };
      })
      .sort((a, b) => {
        return (a.accountCode || '').localeCompare(b.accountCode || '');
      });
  }, [mizanData]);

  // Adjusted Trial Balance verilerini account code'a göre grupla
  const groupedAgacData = useMemo(() => {
    if (!agacData || !Array.isArray(agacData) || agacData.length === 0) return null;

    const groups = new Map();
    const processedCodes = new Set();
    const codeSet = new Set(); // Hızlı lookup için

    // Önce tüm satırları hesap koduna göre grupla ve kod setini oluştur
    agacData.forEach((row) => {
      const accountCode = row.hesapKodu || '';
      if (accountCode) {
        codeSet.add(accountCode);
        if (!groups.has(accountCode)) {
          groups.set(accountCode, {
            accountCode,
            accountName: row.hesapAdi || '',
            baseRow: row,
            subRows: [],
          });
        } else {
          const group = groups.get(accountCode);
          group.subRows.push(row);
        }
      }
    });

    // Prefix ilişkilerini bul ve alt kodları ana kodlara bağla - OPTİMİZE EDİLMİŞ VERSİYON
    // Her hesap kodunu sadece bir kez işleyerek O(n) yaklaşımı
    const parentChildMap = new Map(); // parent -> [children] mapping
    
    // Önce tüm parent-child ilişkilerini bul (O(n) yaklaşımı)
    codeSet.forEach(code => {
      // Her kod için olası parent'ları bul
      const parts = code.split('.');
      for (let i = parts.length - 1; i > 0; i--) {
        const potentialParent = parts.slice(0, i).join('.');
        if (codeSet.has(potentialParent)) {
          if (!parentChildMap.has(potentialParent)) {
            parentChildMap.set(potentialParent, []);
          }
          parentChildMap.get(potentialParent).push(code);
          break; // Sadece direkt parent'ı bul (en yakın parent)
        }
      }
    });

    // Parent-child ilişkilerini kullanarak subRows'ları ekle
    parentChildMap.forEach((childCodes, parentCode) => {
      const parentGroup = groups.get(parentCode);
      if (parentGroup) {
        childCodes.forEach(childCode => {
          if (!processedCodes.has(childCode)) {
            const childGroup = groups.get(childCode);
            if (childGroup) {
              parentGroup.subRows.push(childGroup.baseRow);
              if (childGroup.subRows.length > 0) {
                parentGroup.subRows.push(...childGroup.subRows);
              }
              processedCodes.add(childCode);
            }
          }
        });
      }
    });

    // Ana grupları oluştur
    return Array.from(groups.entries())
      .filter(([code]) => !processedCodes.has(code))
      .map(([code, group]) => {
        // Ana hesap kodunun kendi değerini de dahil et
        const baseHamTutar = parseFloat(group.baseRow?.hamTutar) || 0;
        const baseDegisimTutar = parseFloat(group.baseRow?.degisimTutar) || 0;
        const baseNihaiTutar = parseFloat(group.baseRow?.nihaiTutar) || 0;

        // Tüm alt seviyeleri topla (sadece direkt child değil, tüm alt seviyeler)
        // Örn: 120 -> 120.01, 120.01.001, 120.01.001.1 gibi tüm seviyeler
        const allChildRows = group.subRows.filter(row => {
          const rowCode = row.hesapKodu || '';
          // Tüm alt seviyeleri dahil et (parent code ile başlayan tüm kodlar)
          return rowCode.startsWith(code + '.');
        });

        const totalsFromSubRows = allChildRows.reduce(
          (acc, row) => {
            acc.hamTutar += parseFloat(row.hamTutar) || 0;
            acc.degisimTutar += parseFloat(row.degisimTutar) || 0;
            acc.nihaiTutar += parseFloat(row.nihaiTutar) || 0;
            return acc;
          },
          { hamTutar: 0, degisimTutar: 0, nihaiTutar: 0 }
        );

        const hasMultipleDetails = group.subRows.length > 1;
        const hasAnyDetail = group.subRows.length > 0;
        const hasChildren = allChildRows.length > 0;

        // Eğer alt seviye kodlar varsa, ana hesap kodunun değerini de ekle
        const totalHamTutar = hasChildren 
          ? baseHamTutar + totalsFromSubRows.hamTutar 
          : baseHamTutar;
        const totalDegisimTutar = hasChildren 
          ? baseDegisimTutar + totalsFromSubRows.degisimTutar 
          : baseDegisimTutar;
        // nihaiTutar = hamTutar + degisimTutar (backend'de zaten doğru hesaplanıyor, ama frontend'de de kontrol edelim)
        const totalNihaiTutar = hasChildren 
          ? baseNihaiTutar + totalsFromSubRows.nihaiTutar 
          : baseNihaiTutar;

        return {
          ...group,
          totalHamTutar,
          totalDegisimTutar,
          totalNihaiTutar,
          hasMultipleDetails,
          hasAnyDetail,
        };
      })
      .sort((a, b) => (a.accountCode || '').localeCompare(b.accountCode || ''));
  }, [agacData]);

  // Group yevmiyeData by rule (aciklama)
  const groupedYevmiyeData = useMemo(() => {
    if (!yevmiyeData || !Array.isArray(yevmiyeData) || yevmiyeData.length === 0) return [];

    const groups = new Map();
    
    yevmiyeData.forEach((row, idx) => {
      const key = row.aciklama || `Rule ${idx + 1}`;
      if (!groups.has(key)) {
        groups.set(key, {
          aciklama: key,
          tur: row.tur || 'Aktarma-Arındırma',
          rows: []
        });
      }
      groups.get(key).rows.push(row);
    });

    return Array.from(groups.values()).map((group, idx) => ({
      ...group,
      no: idx + 1
    }));
  }, [yevmiyeData]);

  const handleToggleRow = (accountCode) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(accountCode)) {
      newExpanded.delete(accountCode);
    } else {
      newExpanded.add(accountCode);
    }
    setExpandedRows(newExpanded);
  };

  const handleToggleYevmiyeRule = (aciklama) => {
    const newExpanded = new Set(expandedYevmiyeRules);
    if (newExpanded.has(aciklama)) {
      newExpanded.delete(aciklama);
    } else {
      newExpanded.add(aciklama);
    }
    setExpandedYevmiyeRules(newExpanded);
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return <InsertDriveFile />;
    const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    if (extension === '.pdf') {
      return <PictureAsPdf sx={{ color: '#d32f2f' }} />;
    }
    return <Description sx={{ color: 'primary.main' }} />;
  };

  // Excel export functions
  const exportTrialBalanceToExcel = () => {
    if (!mizanData || mizanData.length === 0) {
      toast.warning('No trial balance data to export.');
      return;
    }

    try {
      const toExcelNumber = (value) => {
        if (value === null || value === undefined) return 0;
        const n = parseFloat(value);
        return isNaN(n) ? 0 : Math.round(n * 100) / 100;
      };

      const formatTrDateSlash = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      const periodStart =
        selectedMizanListMeta?.periodCoveredStart ||
        selectedMizanListMeta?.periodStart ||
        selectedMizanDetail?.periodCoveredStart ||
        '';
      const periodEnd =
        selectedMizanListMeta?.periodCoveredEnd ||
        selectedMizanListMeta?.periodEnd ||
        selectedMizanDetail?.periodCoveredEnd ||
        '';
      const startDateStr = formatTrDateSlash(periodStart);
      const endDateStr = formatTrDateSlash(periodEnd);

      const periodText =
        String(
          selectedMizanListMeta?.tarihAraligi ||
          selectedMizanListMeta?.dateRange ||
          selectedMizanListMeta?.periodRange ||
          ''
        ).trim() ||
        (startDateStr && endDateStr ? `${startDateStr}-${endDateStr}` : '') ||
        (periodStart && periodEnd ? `${periodStart}-${periodEnd}` : '');
      console.log('[MizanReading][ExportExcel] Period/date range debug', {
        periodStart,
        periodEnd,
        startDateStr,
        endDateStr,
        periodText,
        selectedMizanDetail,
        selectedMizanListMeta,
        externalViewingDateRange,
        externalViewingPeriodStart,
        externalViewingPeriodEnd,
      });

      const companyTitle = selectedCompany?.name ||
        selectedCompany?.title ||
        selectedCompany?.companyName ||
        selectedCompany?.unvan ||
        '';

      const headerRow = [
        'HESAP KODU',
        'HESAP ADI',
        'BORÇ HAREKETLERİ TOPLAMI',
        'ALACAK HAREKETLERİ TOPLAMI',
        'BORÇ BAKİYESİ',
        'ALACAK BAKİYESİ',
      ];

      // TrialBalance (General Journal) görünümüne yakın header + tablo düzeni
      const rows = [
        ['MİZAN', '', '', '', '', ''],
        [companyTitle, '', '', '', '', ''],
        [
          'Dönem :',
          periodText,
          '', '', '', ''
        ],
        [
          'Tarih Aralığı :',
          periodText,
          '', '', '', ''
        ],
        ['', '', '', '', '', ''],
        headerRow,
      ];
      const rowLevelMap = new Map();

      const pushGroupRows = () => {
        if (groupedMizanData && Array.isArray(groupedMizanData)) {
          groupedMizanData.forEach((group) => {
            const mainCode = String(group.accountCode || '').trim();
            const mainLevel = mainCode ? Math.min(mainCode.split('.')[0].length, 3) : 3;
            rows.push([
              group.accountCode || '',
              group.accountName || '',
              toExcelNumber(group.totalBorcToplami),
              toExcelNumber(group.totalAlacakToplami),
              toExcelNumber(group.totalBorcBakiyesi),
              toExcelNumber(group.totalAlacakBakiyesi),
            ]);
            rowLevelMap.set(rows.length - 1, mainLevel);

            if (group.subRows && group.subRows.length > 0) {
              group.subRows.forEach((row) => {
                rows.push([
                  row.hesapKodu || '',
                  row.hesapAdi || '',
                  toExcelNumber(row.borcToplami),
                  toExcelNumber(row.alacakToplami),
                  toExcelNumber(row.borcBakiyesi),
                  toExcelNumber(row.alacakBakiyesi),
                ]);
                rowLevelMap.set(rows.length - 1, 4);
              });
            }
          });
        } else {
          mizanData.forEach((row) => {
            const code = String(row.hesapKodu || '').trim();
            const level = code ? Math.min(code.split('.')[0].length, 3) : 3;
            rows.push([
              row.hesapKodu || '',
              row.hesapAdi || '',
              toExcelNumber(row.borcToplami),
              toExcelNumber(row.alacakToplami),
              toExcelNumber(row.borcBakiyesi),
              toExcelNumber(row.alacakBakiyesi),
            ]);
            rowLevelMap.set(rows.length - 1, level);
          });
        }
      };

      pushGroupRows();

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
        { s: { r: 2, c: 1 }, e: { r: 2, c: 5 } },
        { s: { r: 3, c: 1 }, e: { r: 3, c: 5 } },
      ];
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      for (let c = 0; c <= 5; c++) {
        const ref1 = XLSX.utils.encode_cell({ r: 0, c });
        const ref2 = XLSX.utils.encode_cell({ r: 1, c });
        if (!ws[ref1]) ws[ref1] = { t: 's', v: '' };
        if (!ws[ref2]) ws[ref2] = { t: 's', v: '' };
        ws[ref1].s = { alignment: { horizontal: 'center', vertical: 'center' }, font: { bold: true } };
        ws[ref2].s = { alignment: { horizontal: 'center', vertical: 'center' }, font: { bold: true } };
      }
      const periodValueRef = XLSX.utils.encode_cell({ r: 2, c: 1 });
      const dateRangeValueRef = XLSX.utils.encode_cell({ r: 3, c: 1 });
      ws[periodValueRef] = {
        ...(ws[periodValueRef] || {}),
        t: 's',
        v: periodText,
        z: '@',
      };
      ws[dateRangeValueRef] = {
        ...(ws[dateRangeValueRef] || {}),
        t: 's',
        v: periodText,
        z: '@',
      };
      ws[periodValueRef].s = {
        ...(ws[periodValueRef].s || {}),
        alignment: { horizontal: 'left', vertical: 'center' },
      };
      ws[dateRangeValueRef].s = {
        ...(ws[dateRangeValueRef].s || {}),
        alignment: { horizontal: 'left', vertical: 'center' },
      };

      for (let c = range.s.c; c <= range.e.c; c++) {
        const ref = XLSX.utils.encode_cell({ r: 5, c });
        if (ws[ref]) {
          ws[ref].t = 's';
          ws[ref].z = '@';
          if (typeof ws[ref].v !== 'string') ws[ref].v = String(ws[ref].v ?? '');
          ws[ref].s = {
            ...(ws[ref].s || {}),
            font: { bold: true, color: { rgb: '000000' } },
            fill: { fgColor: { rgb: 'A6A6A6' } },
            alignment: { horizontal: 'center', vertical: 'center' },
          };
        }
      }

      const thickBorder = {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      };
      for (let r = range.s.r; r <= range.e.r; r++) {
        for (let c = range.s.c; c <= range.e.c; c++) {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (!ws[ref]) continue;
          ws[ref].s = { ...(ws[ref].s || {}), border: thickBorder };
        }
      }

      [2, 3, 4, 5].forEach((col) => {
        for (let r = 6; r <= range.e.r; r++) {
          const ref = XLSX.utils.encode_cell({ r, c: col });
          if (ws[ref] && typeof ws[ref].v === 'number') {
            ws[ref].t = 'n';
            ws[ref].z = '#,##0.00';
          }
        }
      });

      ws['!cols'] = [
        { wch: 18 },
        { wch: 40 },
        { wch: 22 },
        { wch: 22 },
        { wch: 20 },
        { wch: 20 },
      ];

      for (let r = 6; r <= range.e.r; r++) {
        const level = rowLevelMap.get(r);
        if (!level) continue;

        let styleForLevel = null;
        if (level === 1) {
          styleForLevel = {
            font: { bold: true },
            fill: { fgColor: { rgb: 'D9D9D9' } },
            alignment: { vertical: 'center' },
          };
        } else if (level === 2) {
          styleForLevel = {
            font: { bold: true },
            fill: { fgColor: { rgb: 'EDEDED' } },
            alignment: { vertical: 'center' },
          };
        } else if (level === 3) {
          styleForLevel = {
            font: { bold: true },
            fill: { fgColor: { rgb: 'F3F3F3' } },
            alignment: { vertical: 'center' },
          };
        } else if (level === 4) {
          styleForLevel = {
            font: { bold: false, color: { rgb: '333333' } },
            alignment: { vertical: 'center' },
          };
        }

        if (!styleForLevel) continue;
        for (let c = 0; c <= 5; c++) {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (!ws[ref]) continue;
          ws[ref].s = { ...(ws[ref].s || {}), ...styleForLevel };
        }
      }

      for (let r = 6; r <= range.e.r; r++) {
        for (let c = 2; c <= 5; c++) {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (ws[ref] && typeof ws[ref].v === 'number') {
            ws[ref].t = 'n';
            ws[ref].z = '#,##0.00';
            ws[ref].s = {
              ...(ws[ref].s || {}),
              alignment: { horizontal: 'right', vertical: 'center' },
            };
          }
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Mizan');
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `trial_balance_${timestamp}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success('Trial balance exported to Excel successfully.');
    } catch (error) {
      console.error('Error exporting trial balance to Excel:', error);
      toast.error('Failed to export trial balance to Excel.');
    }
  };

  const exportAdjustedTrialBalanceToExcel = () => {
    if (!agacData || agacData.length === 0) {
      toast.warning('No adjusted trial balance data to export.');
      return;
    }

    try {
      // Prepare data for Excel
      const excelData = [];
      
      // Add headers
      excelData.push([
        'Account Code',
        'Account Name',
        'Original Balance',
        'Adjustment',
        'Final Balance'
      ]);

      // Add data rows
      if (groupedAgacData) {
        groupedAgacData.forEach((group) => {
          // Add main row
          excelData.push([
            group.accountCode || '',
            group.accountName || '',
            parseFloat(group.totalHamTutar) || 0,
            parseFloat(group.totalDegisimTutar) || 0,
            parseFloat(group.totalNihaiTutar) || 0
          ]);

          // Add sub rows if expanded
          if (group.subRows && group.subRows.length > 0) {
            group.subRows.forEach((row) => {
              excelData.push([
                row.hesapKodu || '',
                row.hesapAdi || '',
                parseFloat(row.hamTutar) || 0,
                parseFloat(row.degisimTutar) || 0,
                parseFloat(row.nihaiTutar) || 0
              ]);
            });
          }
        });
      } else {
        agacData.forEach((row) => {
          excelData.push([
            row.hesapKodu || '',
            row.hesapAdi || '',
            parseFloat(row.hamTutar) || 0,
            parseFloat(row.degisimTutar) || 0,
            parseFloat(row.nihaiTutar) || 0
          ]);
        });
      }

      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Adjusted Trial Balance');

      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `adjusted_trial_balance_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
      toast.success('Adjusted trial balance exported to Excel successfully.');
    } catch (error) {
      console.error('Error exporting adjusted trial balance to Excel:', error);
      toast.error('Failed to export adjusted trial balance to Excel.');
    }
  };

  const exportJournalToExcel = () => {
    if (!yevmiyeData || yevmiyeData.length === 0) {
      toast.warning('No journal data to export.');
      return;
    }

    try {
      // Prepare data for Excel
      const excelData = [];
      
      // Add headers
      excelData.push([
        '#',
        'Account Code',
        'Account Name',
        'Amount',
        'Side',
        'Type',
        'Description'
      ]);

      // Add data rows
      yevmiyeData.forEach((row, idx) => {
        excelData.push([
          row.no || idx + 1,
          row.hesapKodu || '',
          row.hesapAdi || '',
          parseFloat(row.tutar) || 0,
          row.taraf || '',
          row.tur || '',
          row.aciklama || ''
        ]);
      });

      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Journal');

      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `journal_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
      toast.success('Journal exported to Excel successfully.');
    } catch (error) {
      console.error('Error exporting journal to Excel:', error);
      toast.error('Failed to export journal to Excel.');
    }
  };
  const exportBalanceSheetToExcel = () => {
    if (!groupedMizanData || groupedMizanData.length === 0) {
      toast.warning('No balance sheet data to export.');
      return;
    }
  
    try {
      const excelData = [];
      
      const anaHesaplar = groupedMizanData.filter(group => {
        const accountCode = group.accountCode || '';
        return accountCode.indexOf('.') === -1;
      });
  
      const aktifKalemler = anaHesaplar.filter(k => {
        const ilkHane = (k.accountCode || '').charAt(0);
        return ilkHane === '1' || ilkHane === '2';
      });
  
      const pasifKalemler = anaHesaplar.filter(k => {
        const ilkHane = (k.accountCode || '').charAt(0);
        return ilkHane === '3' || ilkHane === '4' || ilkHane === '5';
      });
  
      const totals = {
        donenVarliklar: 0,
        duranVarliklar: 0,
        aktifToplam: 0,
        kisaVadeliYukumlulukler: 0,
        uzunVadeliYukumlulukler: 0,
        sermaye: 0,
        donemNetKari: 0,
        pasifToplam: 0,
      };
  
      aktifKalemler.forEach((group) => {
        const ilkHane = (group.accountCode || '').charAt(0);
        const debitBalance = parseFloat(group.totalBorcBakiyesi || 0);
        const debitTotal = parseFloat(group.totalBorcToplami || 0);
        const creditTotal = parseFloat(group.totalAlacakToplami || 0);
        const creditBalance = parseFloat(group.totalAlacakBakiyesi || 0);
        const bilancoDegeri = debitBalance - creditBalance;
        
        if (ilkHane === '1') {
          totals.donenVarliklar += bilancoDegeri;
        } else if (ilkHane === '2') {
          totals.duranVarliklar += bilancoDegeri;
        }
      });
  
      pasifKalemler.forEach((group) => {
        const accountCode = group.accountCode || '';
        const ilkHane = accountCode.charAt(0);
        const debitBalance = parseFloat(group.totalBorcBakiyesi || 0);
        const creditBalance = parseFloat(group.totalAlacakBakiyesi || 0);
        const bilancoDegeri = creditBalance - debitBalance;
        
        if (ilkHane === '3') {
          totals.kisaVadeliYukumlulukler += bilancoDegeri;
        } else if (ilkHane === '4') {
          totals.uzunVadeliYukumlulukler += bilancoDegeri;
        } else if (ilkHane === '5') {
          if (accountCode.startsWith('590') || accountCode.startsWith('591')) {
            totals.donemNetKari += bilancoDegeri;
          } else {
            totals.sermaye += bilancoDegeri;
          }
        }
      });
  
      totals.aktifToplam = totals.donenVarliklar + totals.duranVarliklar;
      totals.pasifToplam = totals.kisaVadeliYukumlulukler + totals.uzunVadeliYukumlulukler + totals.sermaye + totals.donemNetKari;
  
      excelData.push(['AKTİF', '', 'PASİF', '']);
      excelData.push(['Kod', 'Başlık', 'Tutar', 'Kod', 'Başlık', 'Tutar']);
  
      const maxRows = Math.max(aktifKalemler.length, pasifKalemler.length);
  
      for (let i = 0; i < maxRows; i++) {
        const aktif = aktifKalemler[i];
        const pasif = pasifKalemler[i];
  
        const aktifKod = aktif ? aktif.accountCode || '' : '';
        const aktifBaslik = aktif ? aktif.accountName || '' : '';
        const aktifTutar = aktif ? parseFloat(aktif.totalBorcBakiyesi || 0) - parseFloat(aktif.totalAlacakBakiyesi || 0) : 0;
  
        const pasifKod = pasif ? pasif.accountCode || '' : '';
        const pasifBaslik = pasif ? pasif.accountName || '' : '';
        const pasifTutar = pasif ? parseFloat(pasif.totalAlacakBakiyesi || 0) - parseFloat(pasif.totalBorcBakiyesi || 0) : 0;
  
        excelData.push([aktifKod, aktifBaslik, aktifTutar, pasifKod, pasifBaslik, pasifTutar]);
      }
  
      excelData.push([]);
      excelData.push(['DÖNEN VARLIKLAR', '', totals.donenVarliklar, 'KISA VADELİ YÜKÜMLÜLÜKLER', '', totals.kisaVadeliYukumlulukler]);
      excelData.push(['DURAN VARLIKLAR', '', totals.duranVarliklar, 'UZUN VADELİ YÜKÜMLÜLÜKLER', '', totals.uzunVadeliYukumlulukler]);
      excelData.push(['AKTİF TOPLAMI', '', totals.aktifToplam, 'ÖZKAYNAKLAR', '', totals.sermaye]);
      excelData.push(['', '', '', 'DÖNEM NET KARI/ZARARI', '', totals.donemNetKari]);
      excelData.push(['', '', '', 'PASİF TOPLAMI', '', totals.pasifToplam]);
  
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Balance Sheet');
  
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `balance_sheet_${timestamp}.xlsx`;
  
      XLSX.writeFile(wb, filename);
      toast.success('Balance sheet exported to Excel successfully.');
    } catch (error) {
      console.error('Error exporting balance sheet to Excel:', error);
      toast.error('Failed to export balance sheet to Excel.');
    }
  };
  return (
    <>
      <Box sx={{ minHeight: embedded ? 'unset' : '100vh', px: embedded ? 0 : { xs: 2, sm: 3 } }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          {/* Content */}
          <CardContent sx={{ p: embedded ? 0 : 4 }}>
            
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ order: 2, display: 'none' }}>
                {/* Transfer/Cleaning Rules */}
                <Box sx={{ mb: 4 }}>
              <Box
                onClick={() => setShowTransferCleaningRules(!showTransferCleaningRules)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2,
                  justifyContent: 'flex-start',
                  cursor: 'pointer',
                  borderRadius: 2,
                  p: 1,
                  '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTransferCleaningRules(!showTransferCleaningRules);
                    }}
                  >
                    {showTransferCleaningRules ? (
                      <ExpandMore sx={{ transform: 'rotate(180deg)' }} />
                    ) : (
                      <ExpandMore />
                    )}
                  </IconButton>
                  <Info sx={{ color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="600">
                    Transfer/Cleaning Rules
                  </Typography>
                  <Tooltip title="Rules run in priority order; inactive rules are skipped.">
                    <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                      <Info fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Collapse in={showTransferCleaningRules}>
              {/* Rule Writing Guide - Always visible, collapsible */}
              <Paper 
                variant="outlined" 
                sx={{ 
                  mb: 3,
                  borderColor: 'info.main',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }}
              >
                <Box
                  onClick={() => setShowRuleInfo(!showRuleInfo)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Info sx={{ color: 'info.main' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main' }}>
                      Rule Writing Guide
                    </Typography>
                  </Box>
                  <IconButton size="small" sx={{ p: 0.5 }}>
                    {showRuleInfo ? <ExpandMore sx={{ transform: 'rotate(180deg)' }} /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={showRuleInfo}>
                  <Box sx={{ px: 1.5, pb: 1.5 }}>
                    <Typography variant="body2" component="div" sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.8 }}>
                      <strong>kod("101")</strong> - Returns only account 101<br/>
                      <strong>kodAlt("101")</strong> - Returns all sub-accounts of 101 (101.2024, 101.2024.12, etc.)<br/>
                      <strong>hasSubAccountWithName("101", "2024 VADELI ÇEKLER")</strong> - Checks if 101 has a sub-account containing "2024 VADELI ÇEKLER"<br/>
                      <strong>sumSubAccountBalance("101", "2024 VADELI ÇEKLER", "alacak")</strong> - Sums credit balance of 101's sub-accounts containing "2024 VADELI ÇEKLER"<br/>
                      <br/>
                      <strong>Example Condition:</strong> hasSubAccountWithName("101", "2024 VADELI ÇEKLER")<br/>
                      <strong>Example Amount:</strong> sumSubAccountBalance("101", "2024 VADELI ÇEKLER", "alacak")
                    </Typography>
                  </Box>
                </Collapse>
              </Paper>

              {/* Add/Edit Rule Form - Always visible, collapsible */}
              <Card variant="outlined" sx={{ mb: 3, borderColor: 'primary.main', borderWidth: 2 }}>
                <Box
                  onClick={() => {
                    setShowRuleForm(!showRuleForm);
                    if (!showRuleForm && !ruleForm.id) {
                      resetRuleForm();
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    cursor: 'pointer',
                    borderBottom: showRuleForm ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {ruleForm.id ? 'Edit Rule' : 'Add New Rule'}
                  </Typography>
                  <IconButton size="small">
                    {showRuleForm ? <ExpandMore sx={{ transform: 'rotate(180deg)' }} /> : <Add />}
                  </IconButton>
                </Box>
                <Collapse in={showRuleForm}>
                  <CardContent>
                    {ruleFormContent}
                  </CardContent>
                </Collapse>
              </Card>

              <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%', margin: 0 }}>
  <Grid item xs={12} sx={{ width: '100%', paddingLeft: '0 !important' }}>
    <Card variant="outlined" sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}> 
       <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Rule List
                        </Typography>
                        <IconButton size="small" onClick={fetchRules} disabled={loadingRules}>
                          <Refresh fontSize="small" />
                        </IconButton>
                      </Box>
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Condition</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {loadingRules ? (
                              <TableRow>
                                <TableCell colSpan={7}>
                                  <LinearProgress />
                                </TableCell>
                              </TableRow>
                            ) : rules.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={7}>
                                  <Typography variant="body2" color="text.secondary">No rules defined.</Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              rules.map((rule) => (
                                <TableRow key={rule.id} hover>
                                  <TableCell>{rule.oncelik}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={600}>{rule.aktaranKod}</Typography>
                                    <Typography variant="caption" color="text.secondary">{rule.aktaranYon}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={600}>{rule.aktarilanKod}</Typography>
                                    <Typography variant="caption" color="text.secondary">{rule.aktarilanYon}</Typography>
                                  </TableCell>
                                  <TableCell sx={{ maxWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {rule.kosul}
                                  </TableCell>
                                  <TableCell sx={{ maxWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {rule.tutar}
                                  </TableCell>
                                  <TableCell>
                                    <Switch
                                      size="small"
                                      checked={rule.aktif}
                                      onChange={() => handleToggleActive(rule)}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <IconButton size="small" onClick={() => handleEditRule(rule)}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDeleteRule(rule.id)}>
                                      <DeleteOutline fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Collapse>
            </Box>

              </Box>

              <Box sx={{ order: 1, display: hideUploadedFilesList ? 'none' : 'block' }}>
                <UploadedTrialBalanceFilesList
                  refreshToken={uploadedMizansRefreshToken + uploadedListRefreshToken}
                  onViewMizan={async (selected) => {
                    const mizanId = selected?.mizanId;
                    const periodYear = getYearFromDonem(selected?.donem);
                    if (mizanId) {
                      await handleViewMizan(mizanId, periodYear, selected);
                    }
                  }}
                  onDeletedMizanId={(deletedId) => {
                    if (viewingMizanId === deletedId) {
                      setViewingMizanId(null);
                      setSelectedMizanDetail(null);
                      setSelectedMizanListMeta(null);
                      setMizanData(null);
                      setYevmiyeData(null);
                      setAgacData(null);
                      setMizanTabValue(0);
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Trial Balance Data Table with Tabs */}
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Loading selected trial balance...
                </Typography>
              </Box>
            )}

            {(mizanData && mizanData.length > 0) || (agacData && agacData.length > 0) || (yevmiyeData && yevmiyeData.length > 0) ? (
              <Card variant="outlined" sx={{ mt: 2, mb: 2, overflow: 'hidden', borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <IconButton
                      size="small"
                      onClick={() => setShowTrialBalanceSection(!showTrialBalanceSection)}
                      sx={{ p: 0.5 }}
                    >
                      {showTrialBalanceSection ? (
                        <ExpandMore sx={{ transform: 'rotate(180deg)' }} />
                      ) : (
                        <ExpandMore />
                      )}
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Trial Balance
                    </Typography>
                    {(selectedMizanDetail?.fileName || selectedMizanDetail?.generatedFileName || externalViewingFileName) && (
                      <Typography
                        variant="body2"
                        sx={{
                          px: 1.25,
                          py: 0.25,
                          borderRadius: 1,
                          bgcolor: 'grey.100',
                          color: 'text.secondary',
                          maxWidth: { xs: '100%', md: 700 },
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                        }}
                        title={selectedMizanDetail?.fileName || selectedMizanDetail?.generatedFileName || externalViewingFileName}
                      >
                        {selectedMizanDetail?.fileName || selectedMizanDetail?.generatedFileName || externalViewingFileName}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {loading && (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Loading...
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {mizanTabValue === 0 
                        ? `Total ${mizanData?.length || 0} rows`
                        : mizanTabValue === 1
                        ? `Total ${agacData?.length || 0} accounts`
                        : `Total ${yevmiyeData?.length || 0} rows`
                      }
                    </Typography>
                    {mizanTabValue === 0 && mizanData && mizanData.length > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Download />}
                        onClick={exportTrialBalanceToExcel}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Export Excel
                      </Button>
                    )}
                    {mizanTabValue === 1 && agacData && agacData.length > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Download />}
                        onClick={exportAdjustedTrialBalanceToExcel}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Export Excel
                      </Button>
                    )}
                    {mizanTabValue === 2 && yevmiyeData && yevmiyeData.length > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Download />}
                        onClick={exportJournalToExcel}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Export Excel
                      </Button>
                    )}
                    {mizanTabValue === 3 && groupedMizanData && groupedMizanData.length > 0 && (
  <Button
    variant="outlined"
    size="small"
    startIcon={<Download />}
    onClick={exportBalanceSheetToExcel}
    sx={{
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}
  >
    Export Excel
  </Button>
)}
                  </Box>
                </Box>

                <Collapse in={showTrialBalanceSection}>
                <Tabs 
                  value={mizanTabValue} 
                  onChange={(e, newValue) => setMizanTabValue(newValue)}
                  sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab label="Trial Balance Data" />                  
                  {(agacData && agacData.length > 0) || selectedMizanDetail?._agacListPending ? (
                    <Tab label="Adjusted Trial Balance" />
                  ) : null}
                  {yevmiyeData && yevmiyeData.length > 0 && (
                    <Tab label="Journal (Transfer/Cleaning)" />
                  )}
                  {groupedMizanData && groupedMizanData.length > 0 && (
    <Tab label="Bilanço" />
  )}
                  {groupedMizanData && groupedMizanData.length > 0 && (
                    <Tab label="İlişkili Taraflar" icon={<People />} iconPosition="start" />
                  )}
                </Tabs>

                {/* Trial Balance Data Tab */}
                {mizanTabValue === 0 && mizanData && mizanData.length > 0 && (
                  <Box>

                <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap', width: 50 }}>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          Account Code
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          Account Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          Debit Total
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          Credit Total
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          Debit Balance
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          Credit Balance
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedMizanData ? (
                        groupedMizanData.map((group, groupIndex) => {
                          const isExpanded = expandedRows.has(group.accountCode);
                          const hasMultipleDetails = group.hasMultipleDetails;
                          const hasAnyDetail = group.subRows.length > 0;

                          // Recursive function to render nested rows with proper indentation
                          const renderNestedRows = (parentCode, allRows, depth = 0) => {
                            // Find direct children of this parent
                            const directChildren = allRows.filter((row) => {
                              const rowCode = getRowHierarchyCode(row);
                              if (!rowCode.startsWith(parentCode + '.')) return false;
                              // Effective direct child: arada başka bir ara seviye yoksa direkt çocuk
                              return !allRows.some((other) => {
                                const otherCode = getRowHierarchyCode(other);
                                return otherCode !== rowCode
                                  && otherCode.startsWith(parentCode + '.')
                                  && rowCode.startsWith(otherCode + '.');
                              });
                            });

                            return directChildren.map((row, idx) => {
                              const rowCode = getRowHierarchyCode(row);
                              const childIsExpanded = expandedRows.has(rowCode);
                              // Check if this row has children
                              const hasChildren = allRows.some((r) => {
                                const rCode = getRowHierarchyCode(r);
                                return rCode.startsWith(rowCode + '.');
                              });

                              return (
                                <React.Fragment key={`nested-${rowCode}-${idx}`}>
                                  <TableRow
                                    sx={{
                                      backgroundColor: depth % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'rgba(0, 0, 0, 0.04)',
                                      '&:hover': {
                                        backgroundColor: 'rgba(25, 118, 210, 0.06)',
                                      },
                                    }}
                                  >
                                    <TableCell sx={{ width: 50, pl: 4 + (depth * 4) }}>
                                      {hasChildren && (
                                        <IconButton
                                          size="small"
                                          onClick={() => handleToggleRow(rowCode)}
                                          sx={{ p: 0.5 }}
                                        >
                                          {childIsExpanded ? (
                                            <ExpandMore sx={{ fontSize: 18 }} />
                                          ) : (
                                            <ChevronRight sx={{ fontSize: 18 }} />
                                          )}
                                        </IconButton>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                                      {rowCode}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                                      {row.hesapAdi || ''}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                      {formatNumber(row.borcToplami)}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                      {formatNumber(row.alacakToplami)}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                      {formatNumber(row.borcBakiyesi)}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                      {formatNumber(row.alacakBakiyesi)}
                                    </TableCell>
                                  </TableRow>
                                  {hasChildren && childIsExpanded && (
                                    <TableRow>
                                      <TableCell colSpan={7} sx={{ py: 0, border: 'none' }}>
                                        <Collapse in={childIsExpanded} timeout="auto" unmountOnExit>
                                          <Box sx={{ margin: 0, pl: 2 }}>
                                            <Table size="small">
                                              <TableBody>
                                                {renderNestedRows(rowCode, allRows, depth + 1)}
                                              </TableBody>
                                            </Table>
                                          </Box>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            });
                          };

                          const renderSimpleRow = (rowData) => (
                            <TableRow
                              key={`group-simple-${group.accountCode}-${groupIndex}`}
                              hover
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                },
                                borderLeft: '4px solid transparent',
                              }}
                            >
                              <TableCell sx={{ width: 50 }} />
                              <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                                {rowData.hesapKodu || group.accountCode}
                              </TableCell>
                              <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                                {rowData.hesapAdi || group.accountName}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                {formatNumber(rowData.borcToplami ?? group.totalBorcToplami)}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                {formatNumber(rowData.alacakToplami ?? group.totalAlacakToplami)}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                {formatNumber(rowData.borcBakiyesi ?? group.totalBorcBakiyesi)}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                {formatNumber(rowData.alacakBakiyesi ?? group.totalAlacakBakiyesi)}
                              </TableCell>
                            </TableRow>
                          );

                          if (!hasAnyDetail || (hasAnyDetail && !hasMultipleDetails)) {
                            const rowToRender = group.subRows[0] || group.baseRow || {
                              hesapKodu: group.accountCode,
                              hesapAdi: group.accountName,
                              borcToplami: group.totalBorcToplami,
                              alacakToplami: group.totalAlacakToplami,
                              borcBakiyesi: group.totalBorcBakiyesi,
                              alacakBakiyesi: group.totalAlacakBakiyesi,
                            };
                            return renderSimpleRow(rowToRender);
                          }

                          return (
                            <React.Fragment key={`group-${group.accountCode}-${groupIndex}`}>
                              {/* Ana satır (toplam) */}
                              <TableRow 
                                hover
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                  },
                                  borderLeft: '4px solid transparent',
                                  backgroundColor: 'rgba(25, 118, 210, 0.02)'
                                }}
                              >
                                <TableCell sx={{ width: 50 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleRow(group.accountCode)}
                                    sx={{ p: 0.5 }}
                                  >
                                    {isExpanded ? (
                                      <ExpandMore sx={{ fontSize: 20 }} />
                                    ) : (
                                      <ChevronRight sx={{ fontSize: 20 }} />
                                    )}
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                                  {group.accountCode}
                                </TableCell>
                                <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem', fontWeight: 600 }}>
                                  {group.accountName}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                  {formatNumber(group.totalBorcToplami)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                  {formatNumber(group.totalAlacakToplami)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                  {formatNumber(group.totalBorcBakiyesi)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                  {formatNumber(group.totalAlacakBakiyesi)}
                                </TableCell>
                              </TableRow>
                              
                              {/* Detay satırları (Collapse içinde) */}
                              <TableRow>
                                <TableCell colSpan={7} sx={{ py: 0, border: 'none' }}>
                                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ margin: 0 }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ backgroundColor: 'rgba(25, 118, 210, 0.05)' }}>
                                            <TableCell sx={{ width: 50 }} />
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                              Account Code
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                              Account Name
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                              Debit Total
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                              Credit Total
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                              Debit Balance
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                              Credit Balance
                                            </TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {renderNestedRows(group.accountCode, group.subRows, 0)}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })
                      ) : (
                        mizanData.map((row, index) => (
                          <TableRow
                            key={index}
                            hover
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                              },
                            }}
                          >
                            <TableCell sx={{ width: 50 }} />
                            <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                              {row.hesapKodu || '-'}
                            </TableCell>
                            <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                              {row.hesapAdi || '-'}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                              {formatNumber(row.borcToplami)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                              {formatNumber(row.alacakToplami)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                              {formatNumber(row.borcBakiyesi)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                              {formatNumber(row.alacakBakiyesi)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                  </Box>
                )}

                {/* Adjusted Trial Balance Tab */}
                {mizanTabValue === 1 && agacData && agacData.length > 0 && (
                  <Box>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap', width: 50 }}>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              Account Code
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              Account Name
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              Original Balance
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              Adjustment
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              Final Balance
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {groupedAgacData ? (
                            groupedAgacData.map((group, groupIndex) => {
                              const isExpanded = expandedRows.has(group.accountCode);
                              const hasMultipleDetails = group.hasMultipleDetails;
                              const hasAnyDetail = group.subRows.length > 0;

                              const renderSimpleRow = (rowData) => {
                                const isPositive = rowData.nihaiTutar && parseFloat(rowData.nihaiTutar) >= 0;
                                const hasAdjustment = rowData.degisimTutar && parseFloat(rowData.degisimTutar) !== 0;
                                
                                return (
                                  <TableRow
                                    key={`agac-simple-${group.accountCode}-${groupIndex}`}
                                    hover
                                    sx={{
                                      '&:hover': {
                                        backgroundColor: hasAdjustment ? 'rgba(76, 175, 80, 0.1)' : 'rgba(25, 118, 210, 0.04)',
                                      },
                                      borderLeft: hasAdjustment ? '4px solid #4caf50' : '4px solid transparent',
                                    }}
                                  >
                                    <TableCell sx={{ width: 50 }} />
                                    <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                                      {rowData.hesapKodu || group.accountCode}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                                      {rowData.hesapAdi || group.accountName}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right', fontSize: '0.8rem', color: 'text.secondary' }}>
                                      {formatNumber(rowData.hamTutar ?? group.totalHamTutar)}
                                    </TableCell>
                                    <TableCell sx={{ 
                                      textAlign: 'right', 
                                      fontSize: '0.8rem',
                                      fontWeight: hasAdjustment ? 600 : 400,
                                      color: hasAdjustment ? (parseFloat(rowData.degisimTutar ?? group.totalDegisimTutar) > 0 ? 'success.main' : 'error.main') : 'text.secondary'
                                    }}>
                                      {hasAdjustment ? (parseFloat(rowData.degisimTutar ?? group.totalDegisimTutar) > 0 ? '+' : '') + formatNumber(rowData.degisimTutar ?? group.totalDegisimTutar) : '0.00'}
                                    </TableCell>
                                    <TableCell sx={{ 
                                      textAlign: 'right', 
                                      fontWeight: 700, 
                                      fontSize: '0.8rem',
                                      color: isPositive ? '#1976d2' : '#d32f2f'
                                    }}>
                                      {formatNumber(rowData.nihaiTutar ?? group.totalNihaiTutar)}
                                    </TableCell>
                                  </TableRow>
                                );
                              };

                              if (!hasAnyDetail || (hasAnyDetail && !hasMultipleDetails)) {
                                const rowToRender = group.subRows[0] || group.baseRow || {
                                  hesapKodu: group.accountCode,
                                  hesapAdi: group.accountName,
                                  hamTutar: group.totalHamTutar,
                                  degisimTutar: group.totalDegisimTutar,
                                  nihaiTutar: group.totalNihaiTutar,
                                };
                                return renderSimpleRow(rowToRender);
                              }

                              const hasAdjustment = group.totalDegisimTutar && parseFloat(group.totalDegisimTutar) !== 0;
                              const isPositive = group.totalNihaiTutar && parseFloat(group.totalNihaiTutar) >= 0;

                              return (
                                <React.Fragment key={`agac-group-${group.accountCode}-${groupIndex}`}>
                                  {/* Ana satır (toplam) */}
                                  <TableRow 
                                    hover
                                    sx={{
                                      '&:hover': {
                                        backgroundColor: hasAdjustment ? 'rgba(76, 175, 80, 0.1)' : 'rgba(25, 118, 210, 0.04)',
                                      },
                                      borderLeft: hasAdjustment ? '4px solid #4caf50' : '4px solid transparent',
                                      backgroundColor: hasAdjustment ? 'rgba(76, 175, 80, 0.05)' : 'rgba(25, 118, 210, 0.02)'
                                    }}
                                  >
                                    <TableCell sx={{ width: 50 }}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleToggleRow(group.accountCode)}
                                        sx={{ p: 0.5 }}
                                      >
                                        {isExpanded ? (
                                          <ExpandMore sx={{ fontSize: 20 }} />
                                        ) : (
                                          <ChevronRight sx={{ fontSize: 20 }} />
                                        )}
                                      </IconButton>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                                      {group.accountCode}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem', fontWeight: 600 }}>
                                      {group.accountName}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right', fontSize: '0.8rem', color: 'text.secondary', fontWeight: 600 }}>
                                      {formatNumber(group.totalHamTutar)}
                                    </TableCell>
                                    <TableCell sx={{ 
                                      textAlign: 'right', 
                                      fontSize: '0.8rem',
                                      fontWeight: hasAdjustment ? 600 : 400,
                                      color: hasAdjustment ? (parseFloat(group.totalDegisimTutar) > 0 ? 'success.main' : 'error.main') : 'text.secondary'
                                    }}>
                                      {hasAdjustment ? (parseFloat(group.totalDegisimTutar) > 0 ? '+' : '') + formatNumber(group.totalDegisimTutar) : '0.00'}
                                    </TableCell>
                                    <TableCell sx={{ 
                                      textAlign: 'right', 
                                      fontWeight: 700, 
                                      fontSize: '0.8rem',
                                      color: isPositive ? '#1976d2' : '#d32f2f'
                                    }}>
                                      {formatNumber(group.totalNihaiTutar)}
                                    </TableCell>
                                  </TableRow>
                                  
                                  {/* Detay satırları (Collapse içinde) */}
                                  <TableRow>
                                    <TableCell colSpan={6} sx={{ py: 0, border: 'none' }}>
                                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        <Box sx={{ margin: 0 }}>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.05)' }}>
                                                <TableCell sx={{ width: 50 }} />
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                  Account Code
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                  Account Name
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                  Original Balance
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                  Adjustment
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                  Final Balance
                                                </TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {group.subRows.map((row, rowIndex) => {
                                                const isPositive = row.nihaiTutar && parseFloat(row.nihaiTutar) >= 0;
                                                const hasAdjustment = row.degisimTutar && parseFloat(row.degisimTutar) !== 0;
                                                
                                                return (
                                                  <TableRow 
                                                    key={`agac-detail-${group.accountCode}-${rowIndex}`}
                                                    sx={{
                                                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                      '&:hover': {
                                                        backgroundColor: hasAdjustment ? 'rgba(76, 175, 80, 0.06)' : 'rgba(25, 118, 210, 0.06)',
                                                      },
                                                      borderLeft: hasAdjustment ? '3px solid #4caf50' : '3px solid transparent',
                                                    }}
                                                  >
                                                    <TableCell sx={{ width: 50, pl: 4 }}></TableCell>
                                                    <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                                                      {row.hesapKodu || ''}
                                                    </TableCell>
                                                    <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                                                      {row.hesapAdi || ''}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontSize: '0.8rem', color: 'text.secondary' }}>
                                                      {formatNumber(row.hamTutar)}
                                                    </TableCell>
                                                    <TableCell sx={{ 
                                                      textAlign: 'right', 
                                                      fontSize: '0.8rem',
                                                      fontWeight: hasAdjustment ? 600 : 400,
                                                      color: hasAdjustment ? (parseFloat(row.degisimTutar) > 0 ? 'success.main' : 'error.main') : 'text.secondary'
                                                    }}>
                                                      {hasAdjustment ? (parseFloat(row.degisimTutar) > 0 ? '+' : '') + formatNumber(row.degisimTutar) : '0.00'}
                                                    </TableCell>
                                                    <TableCell sx={{ 
                                                      textAlign: 'right', 
                                                      fontWeight: 700, 
                                                      fontSize: '0.8rem',
                                                      color: isPositive ? '#1976d2' : '#d32f2f'
                                                    }}>
                                                      {formatNumber(row.nihaiTutar)}
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              })}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              );
                            })
                          ) : (
                            agacData.map((row, index) => {
                              const isPositive = row.nihaiTutar && parseFloat(row.nihaiTutar) >= 0;
                              const hasAdjustment = row.degisimTutar && parseFloat(row.degisimTutar) !== 0;
                              
                              return (
                                <TableRow
                                  key={`agac-${row.hesapKodu}-${index}`}
                                  hover
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: hasAdjustment ? 'rgba(76, 175, 80, 0.1)' : 'rgba(25, 118, 210, 0.04)',
                                    },
                                    borderLeft: hasAdjustment ? '4px solid #4caf50' : '4px solid transparent',
                                  }}
                                >
                                  <TableCell sx={{ width: 50 }} />
                                  <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                                    {row.hesapKodu || '-'}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                                    {row.hesapAdi || '-'}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontSize: '0.8rem', color: 'text.secondary' }}>
                                    {formatNumber(row.hamTutar)}
                                  </TableCell>
                                  <TableCell sx={{ 
                                    textAlign: 'right', 
                                    fontSize: '0.8rem',
                                    fontWeight: hasAdjustment ? 600 : 400,
                                    color: hasAdjustment ? (parseFloat(row.degisimTutar) > 0 ? 'success.main' : 'error.main') : 'text.secondary'
                                  }}>
                                    {hasAdjustment ? (parseFloat(row.degisimTutar) > 0 ? '+' : '') + formatNumber(row.degisimTutar) : '0.00'}
                                  </TableCell>
                                  <TableCell sx={{ 
                                    textAlign: 'right', 
                                    fontWeight: 700, 
                                    fontSize: '0.8rem',
                                    color: isPositive ? '#1976d2' : '#d32f2f'
                                  }}>
                                    {formatNumber(row.nihaiTutar)}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Journal (Transfer/Cleaning) Tab */}
                {mizanTabValue === 2 && selectedMizanDetail && (
                  <Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, width: 50 }}></TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {groupedYevmiyeData && groupedYevmiyeData.length > 0 ? (
                            groupedYevmiyeData.map((group) => {
                              const isExpanded = expandedYevmiyeRules.has(group.aciklama);
                              return (
                                <React.Fragment key={group.aciklama}>
                                  <TableRow
                                    sx={{
                                      '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.02)' },
                                      cursor: 'pointer',
                                      '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' }
                                    }}
                                    onClick={() => handleToggleYevmiyeRule(group.aciklama)}
                                  >
                                    <TableCell>
                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleYevmiyeRule(group.aciklama); }}>
                                        {isExpanded ? <ExpandMore sx={{ transform: 'rotate(180deg)' }} /> : <ChevronRight />}
                                      </IconButton>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{group.no}</TableCell>
                                    <TableCell>{group.tur || '-'}</TableCell>
                                    <TableCell>{group.aciklama || '-'}</TableCell>
                                  </TableRow>
                                  {isExpanded && (
                                    <TableRow>
                                      <TableCell colSpan={4} sx={{ py: 0, border: 'none' }}>
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                          <Box sx={{ margin: 1 }}>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow sx={{ backgroundColor: 'rgba(25, 118, 210, 0.05)' }}>
                                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>#</TableCell>
                                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Account Code</TableCell>
                                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Account Name</TableCell>
                                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>Amount</TableCell>
                                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Side</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {group.rows.map((row, idx) => (
                                                  <TableRow key={`${row.hesapKodu}-${idx}`}>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{row.no || idx + 1}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                                      {row.hesapKodu || '-'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{row.hesapAdi || '-'}</TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 600, fontSize: '0.8rem' }}>
                                                      {formatNumber(row.tutar)}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>{row.taraf || '-'}</TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </Box>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Typography variant="body2" color="text.secondary">
                                  No journal entries produced by rules.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Bilanço Tab */}
{mizanTabValue === 3 && groupedMizanData && groupedMizanData.length > 0 && (() => {
  // Alt hesapları filtrele (sadece ana kodlar: nokta içermeyen)
  const anaHesaplar = groupedMizanData.filter(group => {
    const accountCode = group.accountCode || '';
    return accountCode.indexOf('.') === -1;
  });

  if (anaHesaplar.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="body2" color="text.secondary">
          Bilanço verisi bulunamadı
        </Typography>
      </Box>
    );
  }

  // Toplamları hesapla
  const totals = {
    donenVarliklar: 0,
    duranVarliklar: 0,
    aktifToplam: 0,
    kisaVadeliYukumlulukler: 0,
    uzunVadeliYukumlulukler: 0,
    sermaye: 0,
    donemNetKari: 0,
    pasifToplam: 0,
    gelirler: 0,
    giderler: 0,
  };

  // Kalemleri işle ve toplamları hesapla
  const kalemler = [];
  anaHesaplar.forEach((group, index) => {
    const accountCode = group.accountCode || '';
    const ilkHane = accountCode.charAt(0);
    const debitBalance = parseFloat(group.totalBorcBakiyesi || 0);
    const creditBalance = parseFloat(group.totalAlacakBakiyesi || 0);
    
    const debitTotal = parseFloat(group.totalBorcToplami || 0);
    const creditTotal = parseFloat(group.totalAlacakToplami || 0);
    let bilancoDegeri = 0;
    
    // Aktif hesaplar (1 ve 2 ile başlayan): debit - credit
    if (ilkHane === '1' || ilkHane === '2') {
      bilancoDegeri = debitBalance - creditBalance;
      
      if (ilkHane === '1') {
        totals.donenVarliklar += bilancoDegeri;
      } else if (ilkHane === '2') {
        totals.duranVarliklar += bilancoDegeri;
      }
    }
    // Pasif hesaplar (3, 4, 5 ile başlayan): credit - debit
    else if (ilkHane === '3' || ilkHane === '4' || ilkHane === '5') {
      bilancoDegeri = creditBalance - debitBalance;
      
      if (ilkHane === '3') {
        totals.kisaVadeliYukumlulukler += bilancoDegeri;
      } else if (ilkHane === '4') {
        totals.uzunVadeliYukumlulukler += bilancoDegeri;
      } else if (ilkHane === '5') {
        // 590 veya 591 kontrolü
        if (accountCode.startsWith('590') || accountCode.startsWith('591')) {
          totals.donemNetKari += bilancoDegeri;
        } else {
          totals.sermaye += bilancoDegeri;
        }
      }
    }
    // Gelir hesapları (6 ile başlayan): credit - debit
    else if (ilkHane === '6') {
      const diff = creditBalance - debitBalance;
      // TrialBalance (General Journal) ile aynı negatif aralık kuralı:
      // 610-639, 650-669, 680-689 ve 691 negatif kabul edilir.
      const codeNum = parseFloat(accountCode) || 0;
      const isContraRevenuePrefix =
        accountCode.startsWith('63') ||
        accountCode.startsWith('65') ||
        accountCode.startsWith('68') ||
        accountCode.startsWith('690');
      const isContraRevenue =
        isContraRevenuePrefix ||
        (codeNum >= 610 && codeNum <= 639) ||
        (codeNum >= 650 && codeNum <= 669) ||
        (codeNum >= 680 && codeNum <= 689) ||
        codeNum === 690 ||
        codeNum === 691 ||
        codeNum === 692;
      // Bakiye kapanmışsa (diff=0), hareket toplamından değer üret:
      // - Baskın taraf alacaksa pozitif
      // - Baskın taraf borçsa negatif
      // - Eşitse (kapanış fişi etkisi), önceki davranışa yakın şekilde mutlak tutarı pozitif göster
      if (diff !== 0) {
        bilancoDegeri = isContraRevenue ? -Math.abs(diff) : diff;
      } else if (creditTotal > debitTotal) {
        bilancoDegeri = isContraRevenue ? -creditTotal : creditTotal;
      } else if (debitTotal > creditTotal) {
        bilancoDegeri = -debitTotal;
      } else {
        const equalAmount = Math.max(creditTotal, debitTotal);
        bilancoDegeri = isContraRevenue ? -equalAmount : equalAmount;
      }
      totals.gelirler += bilancoDegeri;
    }
    // Gider hesapları (7 ile başlayan): debit - credit
    else if (ilkHane === '7') {
      bilancoDegeri = debitBalance - creditBalance;
      totals.giderler += bilancoDegeri;
    }

    kalemler.push({ 
      type: 'kalem', 
      accountCode,
      accountName: group.accountName || '',
      bilancoDegeri,
      ilkHane,
      index
    });
  });

  // Dönem Net Karı/Zararı hesapla (Gelirler - Giderler)
  const hesaplananDonemNetKari = totals.gelirler - totals.giderler;
  
  // Dönem net karının %25'ini hesapla (sadece kar durumunda, 360 hesabına eklenecek)
  const vergiMatrahi = hesaplananDonemNetKari > 0 ? hesaplananDonemNetKari * 0.25 : 0;
  
  // 360 hesabını bul ve değerine ekle
  const hesap360 = kalemler.find(k => k.accountCode === '360' || k.accountCode.startsWith('360.'));
  if (hesap360) {
    hesap360.bilancoDegeri += vergiMatrahi;
    totals.kisaVadeliYukumlulukler += vergiMatrahi;
  } else {
    totals.kisaVadeliYukumlulukler += vergiMatrahi;
  }
  
  // 590/591 değeri = Dönem net karı - Vergi matrahı
  const donemNetKariDegeri = hesaplananDonemNetKari - vergiMatrahi;
  const donemNetKariDegeri2 = - vergiMatrahi;
  
  // Aktif toplamı sadece ana hesap 1 ve 2'nin değerlerinden gelsin.
  // Böylece alt kırılım + üst hesap birlikte geldiğinde double-count engellenir.
  const hesap1 = kalemler.find((k) => k.type === 'kalem' && k.accountCode === '1');
  const hesap2 = kalemler.find((k) => k.type === 'kalem' && k.accountCode === '2');
  totals.aktifToplam = (hesap1?.bilancoDegeri || 0) + (hesap2?.bilancoDegeri || 0);
  // Pasif toplamı da sadece ana hesap 3, 4 ve 5'in değerlerinden gelsin.
  // Böylece alt kırılım + üst hesap birlikte geldiğinde double-count engellenir.
  const hesap3 = kalemler.find((k) => k.type === 'kalem' && k.accountCode === '3');
  const hesap4 = kalemler.find((k) => k.type === 'kalem' && k.accountCode === '4');
  const hesap5 = kalemler.find((k) => k.type === 'kalem' && k.accountCode === '5');
  totals.pasifToplam =
    (hesap3?.bilancoDegeri || 0) +
    (hesap4?.bilancoDegeri || 0) +
    (hesap5?.bilancoDegeri || 0);

  // Toplam satırlarını oluştur
const toplamSatirlari = [
  { type: 'toplam', kod: '', baslik: 'DÖNEN VARLIKLAR', deger: totals.donenVarliklar, isBold: false, grup: 'aktif' },
  { type: 'toplam', kod: '', baslik: 'DURAN VARLIKLAR', deger: totals.duranVarliklar, isBold: false, grup: 'aktif' },
  { type: 'toplam', kod: '', baslik: 'AKTİF TOPLAMI', deger: totals.aktifToplam, isBold: true, grup: 'aktif' },
  { type: 'toplam', kod: '', baslik: 'KISA VADELİ YÜKÜMLÜLÜKLER', deger: totals.kisaVadeliYukumlulukler, isBold: false, grup: 'pasif' },
  { type: 'toplam', kod: '', baslik: 'UZUN VADELİ YÜKÜMLÜLÜKLER', deger: totals.uzunVadeliYukumlulukler, isBold: false, grup: 'pasif' },
  { type: 'toplam', kod: '', baslik: 'ÖZKAYNAKLAR', deger: totals.sermaye + donemNetKariDegeri, isBold: false, grup: 'pasif' },
  { type: 'toplam', kod: '', baslik: 'PASİF TOPLAMI', deger: totals.pasifToplam, isBold: true, grup: 'pasif' },
];

  // Aktif ve pasif kalemlerini ayır
  const aktifKalemler = kalemler.filter(k => k.ilkHane === '1' || k.ilkHane === '2');
  const pasifKalemler = kalemler.filter(k => k.ilkHane === '3' || k.ilkHane === '4' || k.ilkHane === '5');

  // Aktif ve pasif sonuçlarını hazırla
  const prepareSonuc = (kalemlerList, toplamSatirlariList, grup) => {
    const sonuc = [];
    const grupKalemler = grup === 'aktif' ? aktifKalemler : pasifKalemler;
    
    if (grup === 'aktif') {
      let donenVarliklarToplamEklendi = false;
      let duranVarliklarToplamEklendi = false;

      grupKalemler.forEach((item, idx) => {
        sonuc.push(item);
        
        if (!donenVarliklarToplamEklendi && item.ilkHane === '2' && idx > 0 && grupKalemler[idx - 1].ilkHane === '1') {
          sonuc.splice(sonuc.length - 1, 0, toplamSatirlariList[0]);
          donenVarliklarToplamEklendi = true;
        }
      });

      if (!donenVarliklarToplamEklendi && grupKalemler.some(k => k.ilkHane === '1')) {
        let sonDonenIndex = -1;
        for (let i = grupKalemler.length - 1; i >= 0; i--) {
          if (grupKalemler[i].ilkHane === '1') {
            sonDonenIndex = i;
            break;
          }
        }
        if (sonDonenIndex >= 0) {
          let sonucIndex = -1;
          for (let i = 0; i < sonuc.length; i++) {
            if (sonuc[i].type === 'kalem' && sonuc[i].index === grupKalemler[sonDonenIndex].index) {
              sonucIndex = i;
              break;
            }
          }
          if (sonucIndex >= 0) {
            sonuc.splice(sonucIndex + 1, 0, toplamSatirlariList[0]);
          }
        }
      }

      if (!duranVarliklarToplamEklendi && grupKalemler.some(k => k.ilkHane === '2')) {
        let sonDuranIndex = -1;
        for (let i = sonuc.length - 1; i >= 0; i--) {
          if (sonuc[i].type === 'kalem' && sonuc[i].ilkHane === '2') {
            sonDuranIndex = i;
            break;
          }
        }
        if (sonDuranIndex >= 0) {
          sonuc.splice(sonDuranIndex + 1, 0, toplamSatirlariList[1]);
        }
      }
    } else {
      // Pasif için
      let kisaVadeliToplamEklendi = false;
      let uzunVadeliToplamEklendi = false;
      let sermayeToplamEklendi = false;
    
      grupKalemler.forEach((item, idx) => {
        sonuc.push(item);
        
        if (!kisaVadeliToplamEklendi && item.ilkHane === '4' && idx > 0 && grupKalemler[idx - 1].ilkHane === '3') {
          sonuc.splice(sonuc.length - 1, 0, toplamSatirlariList[0]);
          kisaVadeliToplamEklendi = true;
        }
    
        if (!uzunVadeliToplamEklendi && item.ilkHane === '5' && !item.accountCode.startsWith('590') && !item.accountCode.startsWith('591') && idx > 0 && grupKalemler[idx - 1].ilkHane === '4') {
          sonuc.splice(sonuc.length - 1, 0, toplamSatirlariList[1]);
          uzunVadeliToplamEklendi = true;
        }
      });
    
      if (!kisaVadeliToplamEklendi && grupKalemler.some(k => k.ilkHane === '3')) {
        let sonKisaVadeliIndex = -1;
        for (let i = sonuc.length - 1; i >= 0; i--) {
          if (sonuc[i].type === 'kalem' && sonuc[i].ilkHane === '3') {
            sonKisaVadeliIndex = i;
            break;
          }
        }
        if (sonKisaVadeliIndex >= 0) {
          sonuc.splice(sonKisaVadeliIndex + 1, 0, toplamSatirlariList[0]);
        }
      }
    
      if (!uzunVadeliToplamEklendi && grupKalemler.some(k => k.ilkHane === '4')) {
        let sonUzunVadeliIndex = -1;
        for (let i = sonuc.length - 1; i >= 0; i--) {
          if (sonuc[i].type === 'kalem' && sonuc[i].ilkHane === '4') {
            sonUzunVadeliIndex = i;
            break;
          }
        }
        if (sonUzunVadeliIndex >= 0) {
          sonuc.splice(sonUzunVadeliIndex + 1, 0, toplamSatirlariList[1]);
        }
      }
    
      // ÖZKAYNAKLAR'ı ekle (500'lü hesaplardan sonra)
      if (!sermayeToplamEklendi && grupKalemler.some(k => k.ilkHane === '5' && !k.accountCode.startsWith('590') && !k.accountCode.startsWith('591'))) {
        let sonSermayeIndex = -1;
        for (let i = sonuc.length - 1; i >= 0; i--) {
          if (sonuc[i].type === 'kalem' && sonuc[i].ilkHane === '5' && !sonuc[i].accountCode.startsWith('590') && !sonuc[i].accountCode.startsWith('591')) {
            sonSermayeIndex = i;
            break;
          }
        }
        if (sonSermayeIndex >= 0) {
          sonuc.splice(sonSermayeIndex + 1, 0, toplamSatirlariList[2]);
          sermayeToplamEklendi = true;
        }
      }
    }

    return sonuc;
  };

  const aktifSonuc = prepareSonuc(aktifKalemler, [toplamSatirlari[0], toplamSatirlari[1]], 'aktif');
  const pasifSonuc = prepareSonuc(pasifKalemler, [toplamSatirlari[3], toplamSatirlari[4], toplamSatirlari[5]], 'pasif');
  const maxRows = Math.max(aktifSonuc.length, pasifSonuc.length) + 1;

  return (
    <Box sx={{ py: 2 }}>
      <Grid container spacing={3}>
        {/* Aktif Sütunu */}
        <Grid item xs={12} sm={6} md={6} lg={6} sx={{ minWidth: 0, display: 'flex' }}>
          <Card 
            elevation={2}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              width: '100%',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                p: 1.5,
                borderBottom: '2px solid',
                borderColor: 'primary.dark',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', textAlign: 'center' }}>
                AKTİF
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)', overflow: 'auto', width: '100%' }}>
              <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.85rem', whiteSpace: 'nowrap', width: '15%', py: 1.5 }}>
                      Kod
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.85rem', whiteSpace: 'nowrap', py: 1.5 }}>
                      Başlık
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.85rem', whiteSpace: 'nowrap', width: '30%', py: 1.5 }}>
                      Tutar
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {aktifSonuc.map((item, idx) => {
                    const isAktifGrupToplam = item.type === 'kalem' && (item.accountCode === '1' || item.accountCode === '2');
                    const isAktifAltGrup = item.type === 'kalem' && item.accountCode && item.accountCode.length === 2 && (item.accountCode.startsWith('1') || item.accountCode.startsWith('2'));
                    if (isAktifGrupToplam || isAktifAltGrup) {
                      const grupFontWeight = 600;
                      return (
                        <TableRow
                          key={`aktif-${item.accountCode}-${item.index}`}
                          sx={{
                            backgroundColor: 'rgba(25, 118, 210, 0.06)',
                            borderTop: '2px solid',
                            borderColor: 'primary.light',
                            '& td': {
                              fontWeight: grupFontWeight,
                              fontSize: '0.875rem',
                              backgroundColor: 'rgba(25, 118, 210, 0.06)',
                              py: 1.2,
                              borderBottom: 'none',
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: grupFontWeight, color: 'text.primary' }}>
                            {item.accountCode || ''}
                          </TableCell>
                          <TableCell sx={{ fontWeight: grupFontWeight, color: 'text.primary' }}>
                            {item.accountName || ''}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: grupFontWeight, color: 'text.primary', fontFamily: 'monospace' }}>
                            {formatNumber(item.bilancoDegeri || 0)}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    if (item.type === 'kalem') {
                      return (
                        <TableRow 
                          key={`aktif-${item.accountCode}-${item.index}`}
                          hover
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.06)',
                            },
                            transition: 'background-color 0.2s',
                            '& td': {
                              py: 1.2,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }
                          }}
                        >
                          <TableCell sx={{ color: 'primary.main', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                            {item.accountCode || ''}
                          </TableCell>
                          <TableCell sx={{ 
                            color: 'text.primary', 
                            fontSize: '0.875rem',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {item.accountName || ''}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'primary.main', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                            {formatNumber(item.bilancoDegeri || 0)}
                          </TableCell>
                        </TableRow>
                      );
                    } else if (item.type === 'toplam') {
                      return (
                        <TableRow
                          key={`aktif-toplam-${item.baslik}-${idx}`}
                          sx={{
                            backgroundColor: item.isBold ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.06)',
                            borderTop: item.isBold ? '3px solid' : '2px solid',
                            borderColor: item.isBold ? 'primary.main' : 'primary.light',
                            '& td': {
                              fontWeight: item.isBold ? 700 : 600,
                              fontSize: item.isBold ? '0.95rem' : '0.875rem',
                              backgroundColor: item.isBold ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.06)',
                              py: item.isBold ? 1.5 : 1.2,
                              borderBottom: 'none',
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: item.isBold ? 700 : 600, color: item.isBold ? 'primary.main' : 'text.primary' }}>
                            {item.kod || ''}
                          </TableCell>
                          <TableCell sx={{ fontWeight: item.isBold ? 700 : 600, color: item.isBold ? 'primary.main' : 'text.primary' }}>
                            {item.baslik}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: item.isBold ? 700 : 600, color: item.isBold ? 'primary.main' : 'text.primary', fontFamily: 'monospace' }}>
                            {formatNumber(item.deger || 0)}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return null;
                  })}
                  {Array.from({ length: maxRows - aktifSonuc.length - 1 }).map((_, idx) => (
                    <TableRow key={`aktif-empty-${idx}`} sx={{ height: 'auto', visibility: 'hidden' }}>
                      <TableCell colSpan={3} sx={{ height: 0, padding: 0, border: 'none' }} />
                    </TableRow>
                  ))}
                  <TableRow
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.98)',
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 1000,
                      borderTop: '3px solid',
                      borderColor: 'primary.dark',
                      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.15)',
                      '& td': {
                        fontWeight: 700,
                        fontSize: '1rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                        color: 'text.primary',
                        py: 1.8,
                        borderBottom: 'none',
                      }
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}></TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>AKTİF TOPLAMI</TableCell>
                    <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: 'text.primary', fontFamily: 'monospace', fontSize: '1rem' }}>
                      {formatNumber(totals.aktifToplam)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Pasif Sütunu */}
        <Grid item xs={12} sm={6} md={6} lg={6} sx={{ minWidth: 0, display: 'flex' }}>
          <Card 
            elevation={2}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              width: '100%',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                p: 1.5,
                borderBottom: '2px solid',
                borderColor: 'primary.dark',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', textAlign: 'center' }}>
                PASİF
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)', overflow: 'auto', width: '100%' }}>
              <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.85rem', whiteSpace: 'nowrap', width: '15%', py: 1.5 }}>
                      Kod
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.85rem', whiteSpace: 'nowrap', py: 1.5 }}>
                      Başlık
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.85rem', whiteSpace: 'nowrap', width: '30%', py: 1.5 }}>
                      Tutar
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pasifSonuc.map((item, idx) => {
                    const isPasifGrupToplam = item.type === 'kalem' && (item.accountCode === '3' || item.accountCode === '4' || item.accountCode === '5');
                    const isPasifAltGrup = item.type === 'kalem' && item.accountCode && item.accountCode.length === 2 && (item.accountCode.startsWith('3') || item.accountCode.startsWith('4') || item.accountCode.startsWith('5'));
                    if (isPasifGrupToplam || isPasifAltGrup) {
                      const grupFontWeight = 600;
                      return (
                        <TableRow
                          key={`pasif-${item.accountCode}-${item.index}`}
                          sx={{
                            backgroundColor: 'rgba(25, 118, 210, 0.06)',
                            borderTop: '2px solid',
                            borderColor: 'primary.light',
                            '& td': {
                              fontWeight: grupFontWeight,
                              fontSize: '0.875rem',
                              backgroundColor: 'rgba(25, 118, 210, 0.06)',
                              py: 1.2,
                              borderBottom: 'none',
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: grupFontWeight, color: 'text.primary' }}>
                            {item.accountCode || ''}
                          </TableCell>
                          <TableCell sx={{ fontWeight: grupFontWeight, color: 'text.primary' }}>
                            {item.accountName || ''}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: grupFontWeight, color: 'text.primary', fontFamily: 'monospace' }}>
                            {formatNumber(item.bilancoDegeri || 0)}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    if (item.type === 'kalem') {
                      return (
                        <TableRow 
                          key={`pasif-${item.accountCode}-${item.index}`}
                          hover
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.06)',
                            },
                            transition: 'background-color 0.2s',
                            '& td': {
                              py: 1.2,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }
                          }}
                        >
                          <TableCell sx={{ color: 'primary.main', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                            {item.accountCode || ''}
                          </TableCell>
                          <TableCell sx={{ color: 'text.primary', fontSize: '0.875rem' }}>
                            {item.accountName || ''}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'primary.main', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                            {formatNumber(item.bilancoDegeri || 0)}
                          </TableCell>
                        </TableRow>
                      );
                    } else if (item.type === 'toplam') {
                      const isDonemNetKari = item.kod === '590' || item.kod === '591';
                      return (
                        <TableRow
                          key={`pasif-toplam-${item.baslik}-${idx}`}
                          sx={{
                            backgroundColor: item.isBold ? 'rgba(25, 118, 210, 0.12)' : (isDonemNetKari ? 'transparent' : 'rgba(25, 118, 210, 0.06)'),
                            borderTop: item.isBold ? '3px solid' : '2px solid',
                            borderColor: item.isBold ? 'primary.main' : 'primary.light',
                            '& td': {
                              fontWeight: isDonemNetKari ? 400 : (item.isBold ? 700 : 600),
                              fontSize: item.isBold ? '0.95rem' : '0.875rem',
                              backgroundColor: item.isBold ? 'rgba(25, 118, 210, 0.12)' : (isDonemNetKari ? 'transparent' : 'rgba(25, 118, 210, 0.06)'),
                              py: item.isBold ? 1.5 : 1.2,
                              borderBottom: 'none',
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: isDonemNetKari ? 400 : (item.isBold ? 700 : 600), color: item.isBold ? 'primary.main' : 'text.primary' }}>
                            {item.kod || ''}
                          </TableCell>
                          <TableCell sx={{ fontWeight: isDonemNetKari ? 400 : (item.isBold ? 700 : 600), color: item.isBold ? 'primary.main' : 'text.primary' }}>
                            {item.baslik}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: isDonemNetKari ? 400 : (item.isBold ? 700 : 600), color: item.isBold ? 'primary.main' : 'text.primary', fontFamily: 'monospace' }}>
                            {formatNumber(item.deger || 0)}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return null;
                  })}
                  {Array.from({ length: maxRows - pasifSonuc.length - 1 }).map((_, idx) => (
                    <TableRow key={`pasif-empty-${idx}`} sx={{ height: 'auto', visibility: 'hidden' }}>
                      <TableCell colSpan={3} sx={{ height: 0, padding: 0, border: 'none' }} />
                    </TableRow>
                  ))}
                  <TableRow
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.98)',
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 1000,
                      borderTop: '3px solid',
                      borderColor: 'primary.dark',
                      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.15)',
                      '& td': {
                        fontWeight: 700,
                        fontSize: '1rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                        color: 'text.primary',
                        py: 1.8,
                        borderBottom: 'none',
                      }
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}></TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>PASİF TOPLAMI</TableCell>
                    <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: 'text.primary', fontFamily: 'monospace', fontSize: '1rem' }}>
                      {formatNumber(totals.pasifToplam)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Gelir Tablosu Sütunu */}
        <Grid item xs={12} sm={6} md={6} lg={6} sx={{ minWidth: 0, display: 'flex' }}>
          <Card 
            elevation={2}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              width: '100%',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                p: 1.5,
                borderBottom: '2px solid',
                borderColor: 'primary.dark',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', textAlign: 'center' }}>
                GELİR TABLOSU
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)', overflow: 'auto', width: '100%' }}>
              <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.85rem', whiteSpace: 'nowrap', width: '15%', py: 1.5 }}>
                      Kod
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.85rem', whiteSpace: 'nowrap', py: 1.5 }}>
                      Başlık
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.85rem', whiteSpace: 'nowrap', width: '30%', py: 1.5 }}>
                      Tutar
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kalemler
                    .filter(k => k.ilkHane === '6' && k.accountCode !== '6')
                    .map((item, idx) => {
                    const isGelirGrupToplam = false; // "6" ana satırı gelir tablosunda gösterilmemeli
                    const isGelirAltGrup = item.accountCode && item.accountCode.length === 2 && item.accountCode.startsWith('6');
                    if (isGelirGrupToplam || isGelirAltGrup) {
                      const grupFontWeight = 600;
                      return (
                        <TableRow
                          key={`gelir-${item.accountCode}-${item.index}`}
                          sx={{
                            backgroundColor: 'rgba(25, 118, 210, 0.06)',
                            borderTop: '2px solid',
                            borderColor: 'primary.light',
                            '& td': {
                              fontWeight: grupFontWeight,
                              fontSize: '0.875rem',
                              backgroundColor: 'rgba(25, 118, 210, 0.06)',
                              py: 1.2,
                              borderBottom: 'none',
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: grupFontWeight, color: 'text.primary' }}>
                            {item.accountCode || ''}
                          </TableCell>
                          <TableCell sx={{ fontWeight: grupFontWeight, color: 'text.primary' }}>
                            {item.accountName || ''}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: grupFontWeight, color: 'text.primary', fontFamily: 'monospace' }}>
                            {formatNumber(item.bilancoDegeri || 0)}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return (
                      <TableRow 
                        key={`gelir-${item.accountCode}-${item.index}`}
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.06)',
                          },
                          transition: 'background-color 0.2s',
                          '& td': {
                            py: 1.2,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }
                        }}
                      >
                        <TableCell sx={{ color: 'primary.main', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                          {item.accountCode || ''}
                        </TableCell>
                        <TableCell sx={{ color: 'text.primary', fontSize: '0.875rem' }}>
                          {item.accountName || ''}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'primary.main', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                          {formatNumber(item.bilancoDegeri || 0)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
})()}

                {/* İlişkili Taraflar Tab */}
                {mizanTabValue === 4 && (
                  <Box sx={{ p: 2 }}>
                    {companyShareholdersLoading ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          İlişkili taraflar yükleniyor...
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {companyShareholdersError && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            {companyShareholdersError}
                          </Alert>
                        )}
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 280px)' }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Tür</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Ad Soyad</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Pay oranı</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {iliskiliTarafRows.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    Kayıtlı ilişkili taraf bulunamadı.
                                  </TableCell>
                                </TableRow>
                              ) : (
                                iliskiliTarafRows.map((row) => (
                                  <TableRow key={row.id || `${row.adSoyad}-${row.payOrani}`} hover>
                                    <TableCell>{row.tur || '—'}</TableCell>
                                    <TableCell>{row.adSoyad || '—'}</TableCell>
                                    <TableCell>{row.payOrani !== '' && row.payOrani != null ? String(row.payOrani) : '—'}</TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </Box>
                )}
                </Collapse>
              </Box>
                </CardContent>
              </Card>
            ) : null}

          <Card variant="outlined" sx={{ mt: 4, mb: 4, overflow: 'hidden' }}>
              <CardContent sx={{ p: 0 }}>
                <Box
                  onClick={() => setShowTransferCleaningRules(!showTransferCleaningRules)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    justifyContent: 'flex-start',
                    cursor: 'pointer',
                    borderRadius: 2,
                    p: 1,
                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTransferCleaningRules(!showTransferCleaningRules);
                      }}
                    >
                      {showTransferCleaningRules ? (
                        <ExpandMore sx={{ transform: 'rotate(180deg)' }} />
                      ) : (
                        <ExpandMore />
                      )}
                    </IconButton>
                    <Info sx={{ color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Transfer/Cleaning Rules
                    </Typography>
                    <Tooltip title="Rules run in priority order; inactive rules are skipped.">
                      <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                        <Info fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Collapse in={showTransferCleaningRules}>
                  {/* Rule Writing Guide - Always visible, collapsible */}
                  <Paper
                    variant="outlined"
                    sx={{
                      mb: 3,
                      borderColor: 'info.main',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    }}
                  >
                    <Box
                      onClick={() => setShowRuleInfo(!showRuleInfo)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Info sx={{ color: 'info.main' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main' }}>
                          Rule Writing Guide
                        </Typography>
                      </Box>
                      <IconButton size="small" sx={{ p: 0.5 }}>
                        {showRuleInfo ? (
                          <ExpandMore sx={{ transform: 'rotate(180deg)' }} />
                        ) : (
                          <ExpandMore />
                        )}
                      </IconButton>
                    </Box>

                    <Collapse in={showRuleInfo}>
                      <Box sx={{ px: 1.5, pb: 1.5 }}>
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.8 }}
                        >
                          <strong>kod("101")</strong> - Returns only account 101<br />
                          <strong>kodAlt("101")</strong> - Returns all sub-accounts of 101 (101.2024, 101.2024.12, etc.)<br />
                          <strong>hasSubAccountWithName("101", "2024 VADELI ÇEKLER")</strong> - Checks if 101 has a sub-account containing "2024 VADELI ÇEKLER"<br />
                          <strong>sumSubAccountBalance("101", "2024 VADELI ÇEKLER", "alacak")</strong> - Sums credit balance of 101's sub-accounts containing "2024 VADELI ÇEKLER"<br />
                          <br />
                          <strong>Example Condition:</strong> hasSubAccountWithName("101", "2024 VADELI ÇEKLER")<br />
                          <strong>Example Amount:</strong> sumSubAccountBalance("101", "2024 VADELI ÇEKLER", "alacak")
                        </Typography>
                      </Box>
                    </Collapse>
                  </Paper>

                  {/* Add/Edit Rule Form - Always visible, collapsible */}
                  <Card variant="outlined" sx={{ mb: 3, borderColor: 'primary.main', borderWidth: 2 }}>
                    <Box
                      onClick={() => {
                        setShowRuleForm(!showRuleForm);
                        if (!showRuleForm && !ruleForm.id) {
                          resetRuleForm();
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        cursor: 'pointer',
                        borderBottom: showRuleForm ? '1px solid' : 'none',
                        borderColor: 'divider',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        },
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {ruleForm.id ? 'Edit Rule' : 'Add New Rule'}
                      </Typography>
                      <IconButton size="small">
                        {showRuleForm ? (
                          <ExpandMore sx={{ transform: 'rotate(180deg)' }} />
                        ) : (
                          <Add />
                        )}
                      </IconButton>
                    </Box>
                    <Collapse in={showRuleForm}>
                      <CardContent>{ruleFormContent}</CardContent>
                    </Collapse>
                  </Card>

                  <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%', margin: 0 }}>
                    <Grid item xs={12} sx={{ width: '100%', paddingLeft: '0 !important' }}>
                      <Card variant="outlined" sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Rule List
                            </Typography>
                            <IconButton size="small" onClick={fetchRules} disabled={loadingRules}>
                              <Refresh fontSize="small" />
                            </IconButton>
                          </Box>

                          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Condition</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }} align="right">
                                    Actions
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {loadingRules ? (
                                  <TableRow>
                                    <TableCell colSpan={7}>
                                      <LinearProgress />
                                    </TableCell>
                                  </TableRow>
                                ) : rules.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={7}>
                                      <Typography variant="body2" color="text.secondary">
                                        No rules defined.
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  rules.map((rule) => (
                                    <TableRow key={rule.id} hover>
                                      <TableCell>{rule.oncelik}</TableCell>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                          {rule.aktaranKod}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {rule.aktaranYon}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                          {rule.aktarilanKod}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {rule.aktarilanYon}
                                        </Typography>
                                      </TableCell>
                                      <TableCell sx={{ maxWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {rule.kosul}
                                      </TableCell>
                                      <TableCell sx={{ maxWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {rule.tutar}
                                      </TableCell>
                                      <TableCell>
                                        <Switch
                                          size="small"
                                          checked={rule.aktif}
                                          onChange={() => handleToggleActive(rule)}
                                        />
                                      </TableCell>
                                      <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleEditRule(rule)}>
                                          <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDeleteRule(rule.id)}>
                                          <DeleteOutline fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Collapse>
            </CardContent>
          </Card>

          </CardContent>
        </Box>
      </Box>

    </>
  );
};

export default React.memo(MizanReading);
